// ===== IMPORTS =====
import noUiSlider from 'nouislider';
import wNumb from 'wnumb';
import maplibregl from 'maplibre-gl';
import MaplibreGeocoder from '@maplibre/maplibre-gl-geocoder';
import * as turf from '@turf/turf';
import { Chart, registerables } from 'chart.js';
import { tracking } from './tracking';
import {
    incrementSessionCount,
    isDeviceFirstTime,
    daysSinceFirstOpen,
    daysSinceLastOpen,
    recordThisOpen,
    updateGeoCache,
    getCurrentGeo,
    shouldFireInteractionThisSession,
} from './tracking/map-tracking';
import { init as initReviewTrigger, onMushroomClicked as reviewOnMushroomClicked } from './tracking/review-trigger';
import { renderPotentialPanel } from './potential-panel';

// Exposé en global : le panneau est repeint depuis le script inline de mapV5.blade.php,
// au moment où l'utilisateur choisit une espèce.
window.renderPotentialPanel = renderPotentialPanel;

Chart.register(...registerables);

// ===== CONFIGURATION =====
const DEBUG_MODE = false; // Variable de debug
const ADVANCED_SEARCH_RADIUS_METERS = 5000; // Rayon par défaut pour la recherche avancée (en mètres)
const MAP_ADVANCED_BASE_URL = 'https://map-advanced.chasseursdechampignons.com';

const POLYGON_STYLES = {
    default: {
        fillColor: '#aa088f',
        color: '#e80c60',
        fillOpacity: 1,
        stroke: true,
        fill: true,
        opacity: 0.7,
        weight: 2,
    },
    selected: {
        fillColor: '#1d79e8',
        color: '#1d79e8',
        fillOpacity: 0.3,
        stroke: true,
        fill: true,
        opacity: 0.7,
        weight: 1,
    },
};

// Configuration des styles de carte
const MAP_STYLES = {
    standard: {
        version: 8,
        sources: {
            osm: {
                type: 'raster',
                tiles: ['https://a.tile.openstreetmap.org/{z}/{x}/{y}.png'],
                tileSize: 256,
                attribution: '© OpenStreetMap contributors',
            },
        },
        layers: [{
            id: 'osm',
            type: 'raster',
            source: 'osm',
            minzoom: 0,
            maxzoom: 19,
        }],
    },
    satellite: {
        version: 8,
        sources: {
            orthoIGN: {
                type: 'raster',
                tiles: ['https://data.geopf.fr/wmts?REQUEST=GetTile&SERVICE=WMTS&VERSION=1.0.0&TILEMATRIXSET=PM&LAYER=ORTHOIMAGERY.ORTHOPHOTOS&STYLE=normal&FORMAT=image/jpeg&TILECOL={x}&TILEROW={y}&TILEMATRIX={z}'],
                tileSize: 256,
                attribution: '© IGN - Géoportail',
            },
        },
        layers: [{
            id: 'orthoign-layer',
            type: 'raster',
            source: 'orthoIGN',
            minzoom: 0,
            maxzoom: 19,
        }],
    },
};

const BASE_MAPS = {
    standard: { img: 'https://cloud.maptiler.com/static/img/maps/streets.png' },
    satellite: { img: 'https://cloud.maptiler.com/static/img/maps/hybrid.png' },
};

// Variables globales
let currentStyle = 'standard';
let currentLayerName = null;
let vectorGrids = {};
let currentId = null;
let lastRequest = null;
let isFirstSearch = true;
let intervalIdMap = null;

// Variables pour la sélection de point géographique
let isPointSelectionMode = false;
let selectedPoint = null;
let selectedPointMarker = null;
let searchRadiusCircle = null;

// Variable pour stocker le GeoJSON de la carte chargé
let loadedMapGeoJSON = null;

// ===== ANIMATIONS DE CHARGEMENT =====

// Injecter le CSS pour l'animation
const injectLoadingCSS = () => {
    if (document.getElementById('loading-animation-css')) return; // Éviter les doublons
    
    const css = `
        <style id="loading-animation-css">
        .map-loading.loading-animated {
            background: transparent;
            pointer-events: none;
        }
        
        .loading-progress {
            text-align: center;
            color: white;
            max-width: 300px;
            margin: 0 auto;
            pointer-events: none;
            background: rgba(0, 0, 0, 0.7);
            padding: 20px;
            border-radius: 10px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        }
        
        .loading-spinner {
            width: 40px;
            height: 40px;
            border: 4px solid rgba(255, 255, 255, 0.3);
            border-top: 4px solid #007bff;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0 auto 20px;
        }
        
        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
        
        .loading-text {
            font-size: 16px;
            margin-bottom: 15px;
            font-weight: 500;
        }
        
        .loading-bar {
            width: 100%;
            height: 6px;
            background: rgba(255, 255, 255, 0.2);
            border-radius: 3px;
            overflow: hidden;
            margin-bottom: 10px;
        }
        
        .loading-bar-fill {
            height: 100%;
            background: linear-gradient(90deg, #007bff, #00d4ff);
            border-radius: 3px;
            transition: width 0.3s ease;
            position: relative;
        }
        
        .loading-bar-fill::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
            animation: shimmer 2s infinite;
        }
        
        @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
        }
        
        .loading-percentage {
            font-size: 14px;
            font-weight: 600;
            color: #00d4ff;
        }
        </style>
    `;
    
    document.head.insertAdjacentHTML('beforeend', css);
};

// Injecter le CSS au chargement
injectLoadingCSS();

const LoadingAnimation = {
    progressInterval: null,
    
    // Afficher l'animation de chargement
    show: function() {
        const loadingElement = $('.map-loading');
        if (loadingElement.length === 0) return;
        
        // Ajouter les classes d'animation
        loadingElement.addClass('loading-animated');
        
        // Créer l'indicateur de progression si il n'existe pas
        if (loadingElement.find('.loading-progress').length === 0) {
            loadingElement.append(`
                <div class="loading-progress">
                    <div class="loading-spinner"></div>
                    <div class="loading-text">${window.mapLoadingTranslations?.geographicData || 'Chargement des données géographiques...'}</div>
                    <div class="loading-bar">
                        <div class="loading-bar-fill"></div>
                    </div>
                    <div class="loading-percentage">0%</div>
                </div>
            `);
        }
        
        // Démarrer l'animation de la barre de progression
        this.startProgressAnimation();
    },
    
    // Démarrer l'animation de progression
    startProgressAnimation: function() {
        const barFill = $('.loading-bar-fill');
        const percentage = $('.loading-percentage');
        
        let progress = 0;
        const interval = setInterval(() => {
            progress += Math.random() * 15; // Progression aléatoire mais réaliste
            if (progress > 90) progress = 90; // Ne pas aller à 100% avant la fin
            
            barFill.css('width', progress + '%');
            percentage.text(Math.round(progress) + '%');
        }, 100);
        
        // Stocker l'interval pour pouvoir l'arrêter
        this.progressInterval = interval;
    },
    
    // Mettre à jour le progrès avec une valeur spécifique
    updateProgress: function(value, text = null) {
        const barFill = $('.loading-bar-fill');
        const percentage = $('.loading-percentage');
        const loadingText = $('.loading-text');
        
        barFill.css('width', value + '%');
        percentage.text(Math.round(value) + '%');
        
        if (text) {
            loadingText.text(text);
        }
    },
    
    // Terminer l'animation
    complete: function() {
        // Arrêter l'animation de progression
        if (this.progressInterval) {
            clearInterval(this.progressInterval);
            this.progressInterval = null;
        }
        
        // Animation finale
        this.updateProgress(100, window.mapLoadingTranslations?.complete || 'Chargement terminé !');
        
        // Masquer après un court délai
        setTimeout(() => {
            $('.map-loading').removeClass('loading-animated').css('display', 'none');
        }, 500);
    },
    
    // Masquer immédiatement (fallback)
    hide: function() {
        if (this.progressInterval) {
            clearInterval(this.progressInterval);
            this.progressInterval = null;
        }
        $('.map-loading').removeClass('loading-animated').css('display', 'none');
    }
};

// Fonction helper pour afficher l'animation
const showLoadingAnimation = () => {
    LoadingAnimation.show();
};

// ===== UTILITAIRES =====

const Utils = {
    isJson: (str) => {
        try {
            JSON.parse(str);
            return true;
        } catch (e) {
            return false;
        }
    },

    parseRawData: (raw) => {
        if (Utils.isJson(raw)) {
            return JSON.parse(raw);
        }
        // Décodage personnalisé (suppression de quelques caractères en début et fin)
        for (let i = 0; i < 7; i++) {
            if (i < 4) {
                raw = raw.slice(1);
            }
            raw = raw.slice(0, -1);
        }
        return JSON.parse(decodeURIComponent(atob(raw)));
    },


    // Ferme la fenêtre d'information sur la zone
    closeAreaInfo: () => {
        $('.map-info').addClass('d-none');
        $('[data-element="area-info"]').empty();
        if (typeof window.hideBiotopeDetailCard === 'function') {
            window.hideBiotopeDetailCard();
        }
        
        // Supprimer les zones sélectionnées (polygones créés par processZoneGeometries)
        if (map.getSource('clicked-zones')) {
            if (map.getLayer('clicked-zones-layer')) {
                map.removeLayer('clicked-zones-layer');
            }
            if (map.getLayer('clicked-zones-border')) {
                map.removeLayer('clicked-zones-border');
            }
            map.removeSource('clicked-zones');
        }
        
        // Supprimer les sélections dans les vectorGrids
        Object.values(vectorGrids).forEach((vg) => {
            vg.clearSelectedFeatures && vg.clearSelectedFeatures();
        });
    },

    // Fonction récursive pour étendre les bounds
    extendBounds: (bounds, coordinates) => {
        if (typeof coordinates[0] === 'number' && typeof coordinates[1] === 'number') {
            bounds.extend(coordinates);
        } else {
            for (const coord of coordinates) {
                Utils.extendBounds(bounds, coord);
            }
        }
    }
};

// Rendre Utils accessible globalement
window.Utils = Utils;

// ===== CARTE =====

// Ajout du header Authorization pour les appels à map-advanced si l'utilisateur a un abonnement Passion
const map = new maplibregl.Map({
    container: 'map',
    style: MAP_STYLES.standard,
    center: [3.247998308689604, 43.471772026744524],
    zoom: 7,
    minZoom: 5.5,
    maxZoom: 18,
    transformRequest: (url, resourceType) => {
        const options = {};

        // Appliquer uniquement aux requêtes vers map-advanced
        if (
            typeof window !== 'undefined' &&
            window.mapAdvancedAuth &&
            window.mapAdvancedAuth.hasAccess &&
            window.mapAdvancedAuth.token &&
            url.startsWith(MAP_ADVANCED_BASE_URL)
        ) {
            options.headers = {
                ...(options.headers || {}),
                Authorization: `Bearer ${window.mapAdvancedAuth.token}`,
            };
        }

        return {
            url,
            ...options,
        };
    },
});

// Make map globally accessible for inline scripts
window.map = map;

// Debug mode - affichage du zoom
if (DEBUG_MODE) {
    map.on('zoomend', () => {
        console.log('Current zoom level:', map.getZoom());
    });
}

// Désactiver la rotation de la carte
map.dragRotate.disable();
map.keyboard.disable();
map.touchZoomRotate.disableRotation();

// ===== GESTION DU CHARGEMENT PENDANT LE ZOOM =====

// Variables pour gérer le chargement des tiles
let isZoomLoading = false;
let isMoveLoading = false;
let zoomLoadingTimeout = null;
let moveLoadingTimeout = null;
let tilesLoadingCount = 0;
let totalTilesToLoad = 0;
let availableTiles = []; // Liste dynamique des tiles disponibles

// Détecter le début du zoom
// map.on('zoomstart', () => {
//     if (DEBUG_MODE) {
//         console.log('Zoom started');
//     }
    
//     // Démarrer le chargement si on a des tiles personnalisées (dynamique)
//     if (hasCustomTiles()) {
//         startZoomLoading();
//     }
    
//     // Ne pas bloquer le zoom - laisser MapLibre GL gérer le zoom normalement
//     // L'animation de chargement est juste un indicateur visuel
// });

// // Détecter le début du déplacement (pan)
// map.on('movestart', () => {
//     if (DEBUG_MODE) {
//         console.log('Move started');
//     }
    
//     // Démarrer le chargement si on a des tiles personnalisées (dynamique)
//     if (hasCustomTiles()) {
//         startMoveLoading();
//     }
// });

// // Détecter la fin du zoom
// map.on('zoomend', () => {
//     if (DEBUG_MODE) {
//         console.log('Zoom ended');
//     }
    
//     // Ne pas arrêter immédiatement, attendre que les tiles se chargent
//     // Mais permettre le zoom continu
// });

// // Détecter la fin du déplacement
// map.on('moveend', () => {
//     if (DEBUG_MODE) {
//         console.log('Move ended');
//     }
    
//     // Ne pas arrêter immédiatement, attendre que les tiles se chargent
//     // Mais permettre le déplacement continu
// });

// // Détecter les changements de zoom pendant le chargement
// map.on('zoom', () => {
//     if (isZoomLoading) {
//         if (DEBUG_MODE) {
//             console.log('Zoom continued during loading');
//         }
//         // Mettre à jour le message pour indiquer que le zoom continue
//         LoadingAnimation.updateProgress(
//             Math.min(Math.round((tilesLoadingCount / totalTilesToLoad) * 100), 95),
//             'Chargement en cours... (zoom libre)'
//         );
//     }
// });

// // Détecter les changements de position pendant le chargement
// map.on('move', () => {
//     if (isZoomLoading || isMoveLoading) {
//         if (DEBUG_MODE) {
//             console.log('Move continued during loading');
//         }
//         // Mettre à jour le message pour indiquer que le déplacement continue
//         LoadingAnimation.updateProgress(
//             Math.min(Math.round((tilesLoadingCount / totalTilesToLoad) * 100), 95),
//             'Chargement en cours... (navigation libre)'
//         );
//     }
// });

// Détecter quand la carte est au repos (tiles chargées)
map.on('idle', () => {
    if (DEBUG_MODE) {
        console.log('Map idle - tiles should be loaded');
    }
    
    // Arrêter le chargement si on était en train de charger
    if (isZoomLoading) {
        stopZoomLoading();
    }
    if (isMoveLoading) {
        stopMoveLoading();
    }
});

// ===== CHARGEMENT DU GEOJSON DE LA CARTE =====
/**
 * Charge le fichier GeoJSON correspondant à la carte actuelle depuis maps-geojson
 * et l'affiche sur la carte comme une couche de limites
 */
function loadMapGeoJSON() {
    // Récupérer l'item actuel
    const currentItem = typeof window.currentItem !== 'undefined' ? window.currentItem : null;
    
    if (!currentItem) {
        if (DEBUG_MODE) {
            console.log('No currentItem found, skipping GeoJSON load');
        }
        return;
    }
    
    // Utiliser item_map depuis le produit si disponible (depuis la table product)
    let itemMap = null;
    if (typeof window.itemMap !== 'undefined' && window.itemMap) {
        itemMap = window.itemMap;
        
        // Vérifier si itemMap contient déjà un country_code (format: country-region)
        // Si itemMap ne contient pas de tiret ou si la première partie n'est pas un code pays valide, ajouter le country_code
        if (itemMap.indexOf('-') === -1) {
            // Pas de tiret, itemMap est probablement juste la région (ex: "ain")
            // Obtenir le country_code
            let countryCode = null;
            if (typeof window.countryCode !== 'undefined' && window.countryCode) {
                countryCode = window.countryCode.toLowerCase();
            } else if (typeof website !== 'undefined' && website.country && website.country.iso_3166_2) {
                countryCode = website.country.iso_3166_2.toLowerCase();
            } else {
                const appUrl = typeof window.app_url !== 'undefined' ? window.app_url : '';
                const locale = appUrl.split('/')[0] || 'fr';
                countryCode = locale.toLowerCase();
            }
            
            if (countryCode) {
                itemMap = `${countryCode}-${itemMap}`;
                if (DEBUG_MODE) {
                    console.log(`Added country_code to item_map: ${itemMap}`);
                }
            }
        } else {
            // Vérifier si la première partie est un code pays valide (2-3 lettres) ou un code numérique
            const parts = itemMap.split('-');
            if (parts.length >= 2 && /^\d+[a-z]?$/i.test(parts[0])) {
                // Format numérique (ex: "01-ain"), extraire la région et reconstruire avec country_code
                let countryCode = null;
                if (typeof window.countryCode !== 'undefined' && window.countryCode) {
                    countryCode = window.countryCode.toLowerCase();
                } else if (typeof website !== 'undefined' && website.country && website.country.iso_3166_2) {
                    countryCode = website.country.iso_3166_2.toLowerCase();
                } else {
                    const appUrl = typeof window.app_url !== 'undefined' ? window.app_url : '';
                    const locale = appUrl.split('/')[0] || 'fr';
                    countryCode = locale.toLowerCase();
                }
                
                if (countryCode) {
                    const region = parts.slice(1).join('-');
                    itemMap = `${countryCode}-${region}`;
                    if (DEBUG_MODE) {
                        console.log(`Reconstructed item_map with country_code: ${itemMap}`);
                    }
                }
            }
        }
        
        if (DEBUG_MODE) {
            console.log(`Using item_map from product: ${itemMap}`);
        }
    } else {
        // Fallback: déterminer item_map en utilisant le code pays
        itemMap = currentItem;
        
        // Obtenir le code pays depuis website.country.iso_3166_2 en minuscules
        let countryCode = null;
        if (typeof website !== 'undefined' && website.country && website.country.iso_3166_2) {
            countryCode = website.country.iso_3166_2.toLowerCase();
        } else if (typeof window.countryCode !== 'undefined') {
            countryCode = window.countryCode.toLowerCase();
        } else {
            // Essayer de déduire depuis l'URL ou le locale
            const appUrl = typeof window.app_url !== 'undefined' ? window.app_url : '';
            // Les locales peuvent être fr, en, de, it, es, etc.
            const locale = appUrl.split('/')[0] || 'fr';
            countryCode = locale.toLowerCase();
        }
        
        if (countryCode) {
            // Si currentItem contient un tiret, vérifier le format
            if (currentItem.indexOf('-') !== -1) {
                const parts = currentItem.split('-');
                // Si la première partie est un code numérique (ex: "01-ain", "2a-corse-du-sud"), extraire la région
                if (parts.length >= 2 && /^\d+[a-z]?$/i.test(parts[0])) {
                    // Format: XX-region ou XXa-region → construire country-region
                    const region = parts.slice(1).join('-'); // Gérer les régions avec tirets
                    itemMap = `${countryCode}-${region}`;
                    if (DEBUG_MODE) {
                        console.log(`Detected numeric code format: ${currentItem} → extracted region: ${region} → item_map: ${itemMap}`);
                    }
                } else if (parts[0].length === 2 && !/^\d+[a-z]?$/i.test(parts[0])) {
                    // Si la première partie est déjà un code pays (ex: "fr-ain"), vérifier si c'est le bon
                    if (parts[0] === countryCode) {
                        itemMap = currentItem; // Déjà au bon format
                        if (DEBUG_MODE) {
                            console.log(`Already in correct format with country code: ${currentItem}`);
                        }
                    } else {
                        // Code pays différent, reconstruire avec le bon code pays
                        const region = parts.slice(1).join('-');
                        itemMap = `${countryCode}-${region}`;
                        if (DEBUG_MODE) {
                            console.log(`Different country code detected: ${currentItem} → reconstructed: ${itemMap}`);
                        }
                    }
                } else {
                    // Format inconnu, utiliser tel quel
                    itemMap = currentItem;
                    if (DEBUG_MODE) {
                        console.log(`Unknown format, using currentItem as-is: ${currentItem}`);
                    }
                }
            } else {
                // Pas de tiret, construire item_map avec le code pays
                itemMap = `${countryCode}-${currentItem}`;
                if (DEBUG_MODE) {
                    console.log(`No dash in currentItem, constructing: ${itemMap}`);
                }
            }
        } else {
            if (DEBUG_MODE) {
                console.log('Could not determine country code, using currentItem as item_map');
            }
            itemMap = currentItem;
        }
    }
    
    if (DEBUG_MODE) {
        console.log(`Using item_map: ${itemMap} (from currentItem: ${currentItem})`);
    }
    
    // Construire l'URL du fichier GeoJSON
    // Les fichiers dans /public/ sont accessibles directement depuis la racine du site
    const geojsonUrl = `${window.location.origin}/maps-geojson/${itemMap}.geojson`;
    const sourceId = 'map-boundaries';
    const overlaySourceId = 'map-boundaries-overlay';
    const layerIdOverlay = 'map-boundaries-overlay';
    const layerIdBorder = 'map-boundaries-border';
    
    // Supprimer les anciennes couches et sources si elles existent
    if (map.getLayer(layerIdBorder)) {
        map.removeLayer(layerIdBorder);
    }
    if (map.getLayer(layerIdOverlay)) {
        map.removeLayer(layerIdOverlay);
    }
    if (map.getSource(overlaySourceId)) {
        map.removeSource(overlaySourceId);
    }
    if (map.getSource(sourceId)) {
        map.removeSource(sourceId);
    }
    
    // Réinitialiser le GeoJSON chargé avant de charger le nouveau
    loadedMapGeoJSON = null;
    
    // Charger le fichier GeoJSON
    fetch(geojsonUrl)
        .then(response => {
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
        })
        .then(geojsonData => {
            if (DEBUG_MODE) {
                console.log(`✅ GeoJSON loaded for item_map ${itemMap}:`, geojsonData);
            }
            
            // Filtrer les features avec geometry null (ex: be.geojson)
            const validFeatures = (geojsonData.features || []).filter(
                f => f && f.geometry && f.geometry.type
            );
            const sanitizedData = {
                type: 'FeatureCollection',
                features: validFeatures
            };
            
            // Stocker le GeoJSON chargé pour validation des points
            loadedMapGeoJSON = sanitizedData;
            
            // Ajouter la source GeoJSON (utiliser les données filtrées)
            map.addSource(sourceId, {
                type: 'geojson',
                data: sanitizedData
            });
            
            // Si aucune géométrie valide, sauter overlay et fitBounds
            if (validFeatures.length === 0) {
                if (DEBUG_MODE) {
                    console.warn(`No valid geometries in GeoJSON for ${itemMap}, skipping overlay and bounds`);
                }
                return;
            }
            
            // Créer un overlay en dehors de la zone
            // Calculer les bounds de la région
            const bounds = turf.bbox(sanitizedData);
            
            // Créer un grand rectangle qui couvre une zone très étendue
            // Pour que l'overlay soit visible partout, même quand on se déplace sur la carte,
            // on crée un rectangle qui couvre pratiquement le monde entier (ou une zone très large)
            // On utilise les bounds de la carte actuelle si disponibles, sinon on étend largement autour de la région
            let extendedBounds;
            
            try {
                // Essayer d'obtenir les bounds de la carte actuelle
                const mapBounds = map.getBounds();
                if (mapBounds) {
                    // Utiliser les bounds de la carte et les étendre encore plus
                    const ne = mapBounds.getNorthEast();
                    const sw = mapBounds.getSouthWest();
                    
                    // Étendre largement les bounds de la carte (multiplier par un facteur important)
                    const lngRange = ne.lng - sw.lng;
                    const latRange = ne.lat - sw.lat;
                    
                    extendedBounds = [
                        Math.max(-180, sw.lng - lngRange * 5), // Étendre 5x vers l'ouest
                        Math.max(-85, sw.lat - latRange * 5),   // Étendre 5x vers le sud
                        Math.min(180, ne.lng + lngRange * 5),   // Étendre 5x vers l'est
                        Math.min(85, ne.lat + latRange * 5)      // Étendre 5x vers le nord
                    ];
                } else {
                    throw new Error('Map bounds not available');
                }
            } catch (error) {
                // Fallback : créer un rectangle très large autour de la région
                // Utiliser un padding très important pour couvrir toute la carte visible
                // 180 degrés = couvre pratiquement le monde entier en longitude
                // On limite la latitude à -85/85 pour éviter les problèmes de projection aux pôles
                const centerLng = (bounds[0] + bounds[2]) / 2;
                
                // Si la région est en Europe/Afrique (longitude entre -20 et 50), couvrir cette zone
                // Sinon, créer un rectangle très large autour de la région
                if (centerLng >= -20 && centerLng <= 50) {
                    // Pour l'Europe/Afrique, couvrir une zone très large
                    extendedBounds = [
                        -30,  // minLng (couvre jusqu'à l'Atlantique)
                        -20,  // minLat (couvre jusqu'au sud de l'Afrique)
                        60,   // maxLng (couvre jusqu'à l'Asie)
                        75    // maxLat (couvre jusqu'au nord de l'Europe)
                    ];
                } else {
                    // Pour les autres régions, utiliser un padding très large
                    const padding = 90; // degrés - très large
                    extendedBounds = [
                        Math.max(-180, bounds[0] - padding), // minLng (limité à -180)
                        Math.max(-85, bounds[1] - padding),   // minLat (limité à -85)
                        Math.min(180, bounds[2] + padding),    // maxLng (limité à 180)
                        Math.min(85, bounds[3] + padding)      // maxLat (limité à 85)
                    ];
                }
            }
            
            // Créer un rectangle qui couvre la zone étendue
            const worldRect = turf.bboxPolygon(extendedBounds);
            
            // Fusionner toutes les features de la région en un seul polygone
            let regionPolygon = null;
            if (validFeatures.length > 0) {
                // Si on a plusieurs features, les fusionner
                if (validFeatures.length === 1) {
                    regionPolygon = validFeatures[0];
                } else {
                    // Fusionner toutes les features
                    let merged = validFeatures[0];
                    for (let i = 1; i < validFeatures.length; i++) {
                        try {
                            merged = turf.union(merged, validFeatures[i]);
                        } catch (error) {
                            if (DEBUG_MODE) {
                                console.warn('Error merging features:', error);
                            }
                            // En cas d'erreur, utiliser la première feature
                            merged = validFeatures[0];
                            break;
                        }
                    }
                    regionPolygon = merged;
                }
            }
            
            // Créer l'overlay en utilisant une approche avec polygone et trous
            if (regionPolygon && regionPolygon.geometry && worldRect) {
                try {
                    if (DEBUG_MODE) {
                        console.log('Creating overlay, region type:', regionPolygon.geometry.type);
                        console.log('World rect bounds:', extendedBounds);
                    }
                    
                    // Créer un polygone avec la région comme trou intérieur
                    // MapLibre GL supporte les polygones avec des trous (premier ring = extérieur, autres = trous)
                    const worldCoords = worldRect.geometry.coordinates[0];
                    
                    // Extraire les coordonnées de la région (peut être un Polygon ou MultiPolygon)
                    let regionRings = [];
                    if (regionPolygon.geometry.type === 'Polygon') {
                        // Pour un Polygon, prendre tous les rings (le premier est l'extérieur)
                        // Les rings doivent être dans le sens opposé pour créer un trou
                        regionRings = regionPolygon.geometry.coordinates.map(ring => {
                            // Inverser l'ordre des coordonnées pour créer un trou (changer le sens)
                            return ring.slice().reverse();
                        });
                    } else if (regionPolygon.geometry.type === 'MultiPolygon') {
                        // Pour un MultiPolygon, extraire tous les rings de tous les polygones
                        regionRings = regionPolygon.geometry.coordinates.flat().map(ring => {
                            return ring.slice().reverse();
                        });
                    } else if (regionPolygon.geometry.type === 'GeometryCollection') {
                        // Pour une GeometryCollection, extraire les rings de tous les Polygon/MultiPolygon contenus
                        regionPolygon.geometry.geometries.forEach(geom => {
                            if (geom.type === 'Polygon') {
                                geom.coordinates.forEach(ring => {
                                    regionRings.push(ring.slice().reverse());
                                });
                            } else if (geom.type === 'MultiPolygon') {
                                geom.coordinates.flat().forEach(ring => {
                                    regionRings.push(ring.slice().reverse());
                                });
                            }
                        });
                    } else {
                        if (DEBUG_MODE) {
                            console.warn('Unsupported geometry type for overlay:', regionPolygon.geometry.type);
                        }
                    }
                    
                    if (regionRings.length > 0) {
                        // Créer le polygone avec trous
                        const overlayGeoJSON = {
                            type: 'Feature',
                            geometry: {
                                type: 'Polygon',
                                coordinates: [
                                    worldCoords, // Ring extérieur (le grand rectangle)
                                    ...regionRings // Trous intérieurs (la région)
                                ]
                            },
                            properties: {}
                        };
                        
                        if (DEBUG_MODE) {
                            console.log('Overlay GeoJSON created:', overlayGeoJSON);
                            console.log('Number of holes:', regionRings.length);
                        }
                        
                        // Ajouter la source pour l'overlay
                        map.addSource(overlaySourceId, {
                            type: 'geojson',
                            data: overlayGeoJSON
                        });
                        
                        // Ajouter la couche d'overlay (masque sombre en dehors de la zone)
                        // S'assurer que la couche est ajoutée avant la bordure pour que la bordure soit visible
                        map.addLayer({
                            id: layerIdOverlay,
                            type: 'fill',
                            source: overlaySourceId,
                            paint: {
                                'fill-color': '#000000',
                                'fill-opacity': 0.2 // Opacité pour assombrir la zone en dehors
                            }
                        });
                        
                        if (DEBUG_MODE) {
                            console.log('✅ Overlay layer added successfully with polygon holes');
                        }
                    } else {
                        if (DEBUG_MODE) {
                            console.warn('No region rings found, cannot create overlay');
                        }
                    }
                } catch (error) {
                    if (DEBUG_MODE) {
                        console.error('Error creating overlay:', error);
                        console.error('Error details:', error.stack);
                    }
                }
            }
            
            // Ajouter la couche de bordure (contour) par-dessus
            map.addLayer({
                id: layerIdBorder,
                type: 'line',
                source: sourceId,
                paint: {
                    'line-color': '#3388ff',
                    'line-width': 2,
                    'line-opacity': 0.8
                }
            });
            
            // Zoomer sur la région avec un padding de quelques kilomètres
            if (validFeatures.length > 0) {
                try {
                    const bounds = turf.bbox(sanitizedData);
                    // Vérifier que les bounds sont valides (éviter NaN/Infinity)
                    const isValid = bounds[0] >= -180 && bounds[0] <= 180 && bounds[2] >= -180 && bounds[2] <= 180
                        && bounds[1] >= -90 && bounds[1] <= 90 && bounds[3] >= -90 && bounds[3] <= 90
                        && [bounds[0], bounds[1], bounds[2], bounds[3]].every(n => isFinite(n));
                    if (!isValid) throw new Error('Invalid bounds from GeoJSON');
                    
                    // Calculer un padding en degrés (environ 5-10 km selon la latitude)
                    // 1 degré de latitude ≈ 111 km
                    // Pour un padding de ~5-10 km, on utilise environ 0.05-0.1 degré
                    // On ajuste selon la latitude pour avoir un padding plus constant en km
                    const centerLat = (bounds[1] + bounds[3]) / 2;
                    const latPadding = 0.08; // ~9 km en latitude
                    const lngPadding = 0.08 / Math.cos(centerLat * Math.PI / 180); // Ajuster pour la longitude
                    
                    // Ajouter le padding aux bounds
                    const paddedBounds = [
                        bounds[0] - lngPadding, // minLng
                        bounds[1] - latPadding, // minLat
                        bounds[2] + lngPadding, // maxLng
                        bounds[3] + latPadding  // maxLat
                    ];
                    
                    map.fitBounds(
                        [[paddedBounds[0], paddedBounds[1]], [paddedBounds[2], paddedBounds[3]]],
                        {
                            padding: 50, // Padding en pixels pour éviter que les contrôles masquent la vue
                            duration: 1000, // Animation de 1 seconde
                            maxZoom: 12 // Limiter le zoom maximum pour ne pas trop zoomer
                        }
                    );
                    
                    if (DEBUG_MODE) {
                        console.log(`✅ Zoomed to region bounds with padding:`, paddedBounds);
                    }
                } catch (error) {
                    if (DEBUG_MODE) {
                        console.log(`⚠️ Error calculating bounds for zoom:`, error);
                    }
                }
            }
        })
        .catch(error => {
            if (DEBUG_MODE) {
                console.log(`⚠️ GeoJSON not found or error loading for item_map ${itemMap}:`, error.message);
            }
            // Ne pas afficher d'erreur si le fichier n'existe pas, c'est normal pour certaines cartes
        });
}

// Helper: derive how the user arrived at this map page
function deriveMapEntryPoint() {
    const ref = document.referrer || '';
    if (!ref) return 'direct_url';
    if (ref.indexOf('/plans') !== -1) return 'from_plans';
    if (ref.indexOf('/product') !== -1) return 'from_product_page';
    if (ref === window.location.origin || ref === window.location.origin + '/') return 'from_home';
    return 'external_referrer';
}

function fireMapInteraction(interactionType) {
    const geo = getCurrentGeo();
    tracking.mapInteraction({
        mushroomType: window.itemMap || null,
        interactionType: interactionType,
        userPlanForItem: (window.MH_USER_CONTEXT && window.MH_USER_CONTEXT.plan) || 'anonymous',
        viewedRegion: geo.region,
        viewedDept: geo.dept,
    });
}

// Charger le GeoJSON après l'initialisation de la carte
map.on('load', () => {
    // --- Phase 2 tracking: Map Opened + Map Session Returned ---
    (function fireMapOpenedTracking() {
        const sessionCount = incrementSessionCount();
        const deviceFirstTime = isDeviceFirstTime();
        const daysLast = daysSinceLastOpen();
        const daysFirst = daysSinceFirstOpen();
        recordThisOpen();

        const center = map.getCenter();
        const mushroomType = window.itemMap || null;
        const userPlanForItem = (window.MH_USER_CONTEXT && window.MH_USER_CONTEXT.plan) || 'anonymous';

        // Kick off the geo lookup and fire Map Opened after it resolves.
        updateGeoCache(center.lat, center.lng).then(function () {
            const geo = getCurrentGeo();

            tracking.mapOpened({
                mushroomType: mushroomType,
                mapVersion: 'v5',
                isFirstTime: deviceFirstTime,
                userPlanForItem: userPlanForItem,
                viewedRegion: geo.region,
                viewedDept: geo.dept,
                entryPoint: deriveMapEntryPoint(),
                sessionCount: sessionCount,
            });

            if (sessionCount > 1) {
                tracking.mapSessionReturned({
                    mushroomType: mushroomType,
                    userPlanForItem: userPlanForItem,
                    sessionCount: sessionCount,
                    daysSinceFirstOpen: daysFirst,
                    daysSinceLastOpen: daysLast,
                });
            }
        });
    })();

    // --- Review modal trigger init ---
    initReviewTrigger();

    // --- Phase 2 tracking: Map Interaction (first zoom or pan per session) ---
    let lastZoomLevel = map.getZoom();

    map.on('zoomend', function () {
        // Update geo cache on every zoom change.
        const center = map.getCenter();
        updateGeoCache(center.lat, center.lng);

        if (!shouldFireInteractionThisSession()) return;
        const newZoom = map.getZoom();
        const interactionType = newZoom > lastZoomLevel ? 'zoom_in' : 'zoom_out';
        lastZoomLevel = newZoom;
        fireMapInteraction(interactionType);
    });

    map.on('moveend', function () {
        // Update geo cache on every pan.
        const center = map.getCenter();
        updateGeoCache(center.lat, center.lng);

        // Debounced interaction fire.
        if (!shouldFireInteractionThisSession()) return;
        fireMapInteraction('pan');
    });

    // Attendre un peu pour s'assurer que tout est prêt
    setTimeout(() => {
        loadMapGeoJSON();
    }, 500);
});

// Recharger le GeoJSON si l'item change
if (typeof window !== 'undefined') {
    // Observer les changements de currentItem
    let lastItem = window.currentItem;
    setInterval(() => {
        if (window.currentItem && window.currentItem !== lastItem) {
            lastItem = window.currentItem;
            if (DEBUG_MODE) {
                console.log(`Item changed to: ${window.currentItem}, reloading GeoJSON`);
            }
            loadMapGeoJSON();
        }
    }, 1000);
}

// Détecter le rendu des tiles (plus précis)
// map.on('render', () => {
//     if (isZoomLoading || isMoveLoading) {
//         // Vérifier l'existence des couches avant de les interroger (dynamique)
//         const availableLayers = [];
        
//         availableTiles.forEach(tile => {
//             if (map.getLayer(tile.layerId)) {
//                 availableLayers.push(tile.layerId);
//             }
//         });
        
//         if (availableLayers.length > 0) {
//             try {
//                 const visibleTiles = map.queryRenderedFeatures({ layers: availableLayers });
//                 if (visibleTiles.length > 0) {
//                     if (DEBUG_MODE) {
//                         console.log('Tiles are being rendered');
//                     }
//                     // Mettre à jour le progrès
//                     updateLoadingProgress();
//                 }
//             } catch (error) {
//                 if (DEBUG_MODE) {
//                     console.log('Error querying rendered features:', error.message);
//                 }
//             }
//         }
//     }
// });

// Fonction pour vérifier si des tiles personnalisées existent
const hasCustomTiles = () => {
    return availableTiles.some(tile => map.getSource(tile.sourceId));
};

// Fonction pour mettre à jour la liste des tiles disponibles
const updateAvailableTiles = (tiles) => {
    availableTiles = tiles || [];
    if (DEBUG_MODE) {
        console.log('Available tiles updated:', availableTiles.map(t => t.sourceId));
    }
};

// Détecter le chargement des tiles (dynamique)
map.on('sourcedata', (e) => {
    // Vérifier si c'est une de nos tiles personnalisées
    const isCustomTile = availableTiles.some(tile => tile.sourceId === e.sourceId);
    
    if (e.isSourceLoaded && isCustomTile) {
        // Vérifier que la source existe vraiment dans la carte
        if (map.getSource(e.sourceId)) {
            if (DEBUG_MODE) {
                console.log(`Tile source loaded: ${e.sourceId}`);
            }
            
            if (isZoomLoading || isMoveLoading) {
                tilesLoadingCount++;
                updateLoadingProgress();
            }
        } else {
            if (DEBUG_MODE) {
                console.log(`Source ${e.sourceId} reported as loaded but not found in map`);
            }
        }
    }
});

// Fonction pour démarrer le chargement du zoom (non-bloquante)
const startZoomLoading = () => {
    if (isZoomLoading) return; // Éviter les doublons
    
    isZoomLoading = true;
    tilesLoadingCount = 0;
    totalTilesToLoad = availableTiles.length; // Nombre dynamique de sources de tiles
    
    // Afficher l'animation de chargement de manière non-bloquante
    $('.map-loading').css('display', 'flex');
    LoadingAnimation.show();
    LoadingAnimation.updateProgress(0, translations.loading_tiles);
    
    if (DEBUG_MODE) {
        console.log(`Zoom loading started (non-blocking) - ${totalTilesToLoad} tiles to load`);
    }
    
    // Timeout de sécurité plus court (2 secondes max pour le zoom)
    // Le zoom doit rester fluide
    zoomLoadingTimeout = setTimeout(() => {
        if (isZoomLoading) {
            if (DEBUG_MODE) {
                console.log('Zoom loading timeout - forcing completion');
            }
            stopZoomLoading();
        }
    }, 2000);
};

// Fonction pour arrêter le chargement du zoom
const stopZoomLoading = () => {
    if (!isZoomLoading) return;
    
    isZoomLoading = false;
    
    // Nettoyer le timeout
    if (zoomLoadingTimeout) {
        clearTimeout(zoomLoadingTimeout);
        zoomLoadingTimeout = null;
    }
    
    // Masquer l'animation
    LoadingAnimation.complete();
    
    if (DEBUG_MODE) {
        console.log('Zoom loading completed');
    }
};

// Fonction pour démarrer le chargement du déplacement (non-bloquante)
const startMoveLoading = () => {
    if (isMoveLoading) return; // Éviter les doublons
    
    isMoveLoading = true;
    tilesLoadingCount = 0;
    totalTilesToLoad = availableTiles.length; // Nombre dynamique de sources de tiles
    
    // Afficher l'animation de chargement de manière non-bloquante
    $('.map-loading').css('display', 'flex');
    LoadingAnimation.show();
    LoadingAnimation.updateProgress(0, translations.loading_tiles);
    
    if (DEBUG_MODE) {
        console.log(`Move loading started (non-blocking) - ${totalTilesToLoad} tiles to load`);
    }
    
    // Timeout de sécurité plus court (1.5 secondes max pour le déplacement)
    // Le déplacement doit rester très fluide
    moveLoadingTimeout = setTimeout(() => {
        if (isMoveLoading) {
            if (DEBUG_MODE) {
                console.log('Move loading timeout - forcing completion');
            }
            stopMoveLoading();
        }
    }, 1500);
};

// Fonction pour arrêter le chargement du déplacement
const stopMoveLoading = () => {
    if (!isMoveLoading) return;
    
    isMoveLoading = false;
    
    // Nettoyer le timeout
    if (moveLoadingTimeout) {
        clearTimeout(moveLoadingTimeout);
        moveLoadingTimeout = null;
    }
    
    // Masquer l'animation
    LoadingAnimation.complete();
    
    if (DEBUG_MODE) {
        console.log('Move loading completed');
    }
};

// Mettre à jour le progrès du chargement (zoom ou déplacement)
const updateLoadingProgress = () => {
    if (!isZoomLoading && !isMoveLoading) return;
    
    // Vérifier que les éléments DOM existent avant de les utiliser
    if (!$('.loading-bar-fill').length || !$('.loading-percentage').length) {
        if (DEBUG_MODE) {
            console.log('Loading elements not found, skipping progress update');
        }
        return;
    }
    
    // Calculer le progrès basé sur les tiles chargées
    const progress = Math.min(Math.round((tilesLoadingCount / totalTilesToLoad) * 100), 95);
    
    // Message adaptatif selon l'état et le type de chargement
    let message = translations.loading_tiles_count.replace(':loaded', tilesLoadingCount).replace(':total', totalTilesToLoad);
    if (tilesLoadingCount > 0) {
        if (isZoomLoading) {
            message = translations.tiles_loaded_zoom.replace(':loaded', tilesLoadingCount).replace(':total', totalTilesToLoad);
        } else if (isMoveLoading) {
            message = translations.tiles_loaded_move.replace(':loaded', tilesLoadingCount).replace(':total', totalTilesToLoad);
        }
    }
    
    try {
        LoadingAnimation.updateProgress(progress, message);
    } catch (error) {
        if (DEBUG_MODE) {
            console.log('Error updating loading progress:', error.message);
        }
    }
    
    if (DEBUG_MODE) {
        const type = isZoomLoading ? 'zoom' : 'move';
        console.log(`${type} loading progress: ${progress}% (${tilesLoadingCount}/${totalTilesToLoad}) - non-blocking`);
    }
    
    // Si toutes les tiles sont chargées, arrêter le chargement approprié
    if (tilesLoadingCount >= totalTilesToLoad) {
        setTimeout(() => {
            if (isZoomLoading) {
                stopZoomLoading();
            }
            if (isMoveLoading) {
                stopMoveLoading();
            }
        }, 200); // Délai plus court pour ne pas bloquer
    }
};

// Ajuster la vue sur le pays si disponible
if (countryFeature) {
    const countryBounds = turf.bbox(countryFeature);
    if (countryBounds) {
        map.fitBounds(countryBounds, { padding: 20, animate: false });
    }
}



// ===== CONTRÔLES =====

// Géocoder personnalisé via l'API Nominatim
const geocoderApi = {
    forwardGeocode: async (config) => {
        const features = [];
        try {
            const request = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(config.query)}&format=geojson&polygon_geojson=1&addressdetails=1`;
            const response = await fetch(request);
            const geojson = await response.json();
            
            for (const feature of geojson.features) {
                const center = [
                    feature.bbox[0] + (feature.bbox[2] - feature.bbox[0]) / 2,
                    feature.bbox[1] + (feature.bbox[3] - feature.bbox[1]) / 2,
                ];
                features.push({
                    type: 'Feature',
                    geometry: { type: 'Point', coordinates: center },
                    place_name: feature.properties.display_name,
                    properties: feature.properties,
                    text: feature.properties.display_name,
                    place_type: ['place'],
                    center,
                });
            }
        } catch (e) {
            console.error(`Failed to forwardGeocode with error: ${e}`);
        }
        return { features };
    },
};

map.addControl(new MaplibreGeocoder(geocoderApi, { maplibregl, language: currentWebsite.fallback_locale }));

// Contrôle de géolocalisation simplifié
class CustomGeolocateControl {
    constructor(options = {}) {
        this.options = {
            positionOptions: { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 },
            ...options
        };
        this._geolocationWatchID = null;
        this._userMarker = null;
        this._userHasMoved = false;
        this._coneEl = null;
        this._heading = null;
        this._onDeviceOrientationBound = (e) => this._onDeviceOrientation(e);
        this._onMapRotateBound = () => this._updateConeRotation();
        this._onMapMoveStartBound = () => { this._userHasMoved = true; };
    }

    onAdd(map) {
        this._map = map;
        this._container = document.createElement('div');
        this._container.classList.add('maplibregl-ctrl', 'maplibregl-ctrl-group');

        this._geolocateButton = document.createElement('button');
        this._geolocateButton.classList.add('maplibregl-ctrl-icon', 'maplibregl-ctrl-geolocate');
        this._geolocateButton.setAttribute('aria-label', translations.find_my_location);
        this._geolocateButton.innerHTML = '<span class="maplibregl-ctrl-icon" aria-hidden="true"></span>';

        this._geolocateButton.addEventListener('click', () => this.trigger());
        this._container.appendChild(this._geolocateButton);

        this._map.on('movestart', this._onMapMoveStartBound);

        this._map.on('rotate', this._onMapRotateBound);

        return this._container;
    }

    onRemove() {
        if (this._geolocationWatchID !== null) {
            navigator.geolocation.clearWatch(this._geolocationWatchID);
        }
        if (this._userMarker) {
            this._userMarker.remove();
        }
        window.removeEventListener('deviceorientation', this._onDeviceOrientationBound, true);
        if (this._map) {
            this._map.off('rotate', this._onMapRotateBound);
            this._map.off('movestart', this._onMapMoveStartBound);
        }
        this._container.remove();
        this._map = null;
        this._coneEl = null;
    }

    trigger() {
        if (this._geolocationWatchID !== null) {
            navigator.geolocation.clearWatch(this._geolocationWatchID);
            this._geolocationWatchID = null;
            window.removeEventListener('deviceorientation', this._onDeviceOrientationBound, true);
            if (this._coneEl) this._coneEl.style.display = 'none';
            this._heading = null;
            return;
        }

        this._userHasMoved = false;
        this._geolocationWatchID = navigator.geolocation.watchPosition(
            (position) => this._onSuccess(position),
            (error) => this._onError(error),
            this.options.positionOptions
        );

        this._addDeviceOrientationListener();
    }

    async _addDeviceOrientationListener() {
        if (!window.DeviceOrientationEvent) return;

        if (typeof DeviceOrientationEvent.requestPermission === 'function') {
            try {
                const state = await DeviceOrientationEvent.requestPermission();
                if (state === 'granted') {
                    window.addEventListener('deviceorientation', this._onDeviceOrientationBound, true);
                }
            } catch (e) {}
        } else {
            window.addEventListener('deviceorientation', this._onDeviceOrientationBound, true);
        }
    }

    _onDeviceOrientation(event) {
        let heading = null;
        if (event.webkitCompassHeading != null) {
            heading = event.webkitCompassHeading;
        } else if (event.alpha != null) {
            heading = 360 - event.alpha;
        }

        if (heading === null || isNaN(heading)) return;

        this._heading = Math.round(heading);
        this._updateConeRotation();
    }

    _updateConeRotation() {
        if (!this._coneEl || this._heading === null) return;
        const rotation = this._heading - this._map.getBearing();
        requestAnimationFrame(() => {
            if (!this._coneEl) return;
            this._coneEl.style.transform = `translateX(-50%) rotate(${rotation}deg)`;
            this._coneEl.style.display = 'block';
        });
    }

    _onSuccess(position) {
        const { longitude, latitude } = position.coords;

        if (!this._userMarker) {
            this._createUserMarker(longitude, latitude);
        } else {
            this._userMarker.setLngLat([longitude, latitude]);
        }

        if (!this._userHasMoved) {
            this._map.flyTo({ center: [longitude, latitude], zoom: 15 });
        }

        if (DEBUG_MODE) {
            console.log('Geolocation:', { latitude, longitude });
        }
    }

    _onError(error) {
        console.warn('Geolocation error:', error);
    }

    _createUserMarker(longitude, latitude) {
        const markerContainer = document.createElement('div');
        markerContainer.classList.add('maplibregl-user-location', 'maplibregl-marker', 'maplibregl-marker-anchor-center');
        markerContainer.setAttribute('aria-label', 'Map marker');
        markerContainer.setAttribute('role', 'img');

        const cone = document.createElement('div');
        cone.classList.add('maplibregl-user-location-cone');
        cone.innerHTML = `<svg width="16" height="27" viewBox="0 0 48 86" xmlns="http://www.w3.org/2000/svg">
            <path d="M 24,12 L 4,46 Q 3,49 8,49 Q 24,38 40,49 Q 45,49 44,46 Z"
                  fill="#1da1f2" stroke="#1da1f2" stroke-width="1.5"
                  stroke-linejoin="round" stroke-linecap="round"/>
        </svg>`;
        this._coneEl = cone;
        markerContainer.appendChild(cone);

        const dot = document.createElement('div');
        dot.classList.add('maplibregl-user-location-dot');
        markerContainer.appendChild(dot);

        this._userMarker = new maplibregl.Marker({ element: markerContainer })
            .setLngLat([longitude, latitude])
            .addTo(this._map);
    }
}


// Ajout des contrôles à la carte
map.addControl(new CustomGeolocateControl(), 'top-right');
map.addControl(new maplibregl.NavigationControl(), 'top-right');

// Contrôle de changement de style simplifié - un clic sur le thumbnail actif switch directement
class LayerSwitcherControl {
    constructor(options) {
        this.options = { ...options };
        this.container = document.createElement('div');
        this.container.classList.add('maplibregl-ctrl', 'maplibregl-ctrl-basemaps', 'closed');
    }

    onAdd(map) {
        this.map = map;
        const layerIds = Object.keys(this.options.basemaps);

        Object.keys(this.options.basemaps).forEach((layerId) => {
            const base = this.options.basemaps[layerId];
            const imgEl = document.createElement('img');
            imgEl.src = base.img;
            imgEl.classList.add('basemap');
            imgEl.dataset.id = layerId;

            if (this.options.initialBasemap === layerId) {
                imgEl.classList.add('active');
            }

            imgEl.addEventListener('click', () => {
                const isActive = imgEl.classList.contains('active');
                const targetId = isActive
                    ? layerIds.find((id) => id !== layerId) ?? layerIds[0]
                    : layerId;

                const activeElement = this.container.querySelector('.active');
                const targetElement = this.container.querySelector(`[data-id="${targetId}"]`);
                if (activeElement) activeElement.classList.remove('active');
                if (targetElement) targetElement.classList.add('active');

                map.setStyle(targetId === 'standard' ? MAP_STYLES.standard : MAP_STYLES.satellite);
                setTimeout(() => $('.form-map').submit(), 500);

                if (DEBUG_MODE) {
                    console.log('Style changed to:', targetId);
                }
            });

            this.container.appendChild(imgEl);
        });
        return this.container;
    }

    onRemove() {
        this.container.parentNode?.removeChild(this.container);
        this.map = undefined;
    }
}

map.addControl(new LayerSwitcherControl({ basemaps: BASE_MAPS, initialBasemap: currentStyle }), 'top-right');

// ===== MÉTIERS =====

// Détection d'une recherche avancée
const isAdvancedSearch = (params) => {
    const advancedFilters = ['filter[elevation]', 'filter[slope]', 'filter[tree][]', 'filter[aspect][]', 'filter[ph]'];
    return params.some(p => advancedFilters.includes(p.name) && p.value !== '');
};

// Détection d'une recherche géographique
const isGeographicSearch = (params) => {
    return params.some(p => p.name === 'filter[search_lat]' && p.value !== '') && 
           params.some(p => p.name === 'filter[search_lng]' && p.value !== '');
};

// ===== GESTION DE LA SÉLECTION DE POINT GÉOGRAPHIQUE =====

// Activer le mode de sélection de point
const enablePointSelection = () => {
    // Nettoyer d'abord toute sélection précédente pour éviter les conflits
    if (selectedPointMarker) {
        selectedPointMarker.remove();
        selectedPointMarker = null;
    }
    
    // Supprimer les couches et la source du cercle de recherche si elles existent
    if (map.getSource('search-radius')) {
        if (map.getLayer('search-radius-fill')) {
            map.removeLayer('search-radius-fill');
        }
        if (map.getLayer('search-radius-border')) {
            map.removeLayer('search-radius-border');
        }
        map.removeSource('search-radius');
    }
    
    isPointSelectionMode = true;
    map.getCanvas().style.cursor = 'crosshair';
    
    // Changer l'apparence du bouton
    $('#selectPointBtn').prop('disabled', true);

    // Sur mobile, replier la Bottom Sheet pour laisser la carte occuper l'écran
    try {
        if (window.matchMedia && window.matchMedia('(max-width: 767.98px)').matches) {
            const overlay = document.getElementById('card-bottom-sheet-overlay');
            if (overlay) {
                const sheet = overlay.querySelector('.card-bottom-sheet');
                const header = sheet ? sheet.querySelector('.card-bottom-sheet-header') : null;
                if (sheet) {
                    sheet.classList.add('collapsed');
                }
                if (header) {
                    overlay.style.height = `${header.offsetHeight}px`;
                }
            }
        }
    } catch (e) {
        // no-op
    }
    
    // Supprimer l'info-bulle si elle existe déjà pour éviter les doublons
    $('#map-point-tooltip').remove();

    // Supprimer l'ancien gestionnaire d'événement de la carte s'il existe
    if (mapTooltipMouseMoveHandler) {
        map.off('mousemove', mapTooltipMouseMoveHandler);
        mapTooltipMouseMoveHandler = null;
    }

    // Créer et positionner une info-bulle à côté du curseur
    $(document).on('mousemove.pointTooltip', function(e) {
        let tooltip = $('#map-point-tooltip');
        if (tooltip.length === 0) {
            tooltip = $('<div id="map-point-tooltip"></div>')
                .css({
                    'position': 'fixed',
                    'pointer-events': 'none',
                    'z-index': 9999,
                    'background': '#fff',
                    'border': '1px solid #ccc',
                    'border-radius': '6px',
                    'padding': '6px 12px',
                    'box-shadow': '0 2px 8px rgba(0,0,0,0.15)',
                    'color': '#36443b',
                    'font-size': '15px',
                    'font-family': "'Bitter', serif"
                })
                .text(translations.click_map_choose_point);
            $('body').append(tooltip);
        }
        tooltip.css({
            left: e.clientX + 20,
            top: e.clientY - 15
        });
    });

    // Gestionnaire pour mettre à jour le tooltip selon la position sur la carte (mode biotope uniquement)
    if (typeof window.currentMapMode !== 'undefined' && window.currentMapMode === 'biotope') {
        mapTooltipMouseMoveHandler = (e) => {
            const tooltip = $('#map-point-tooltip');
            if (tooltip.length === 0 || !isPointSelectionMode) return;
            
            // Vérifier à nouveau le mode au cas où il aurait changé
            if (typeof window.currentMapMode === 'undefined' || window.currentMapMode !== 'biotope') {
                tooltip
                    .text(translations.click_map_choose_point)
                    .css({
                        'background': '#fff',
                        'border-color': '#ccc',
                        'color': '#36443b'
                    });
                return;
            }
            
            const { lng, lat } = e.lngLat;
            const isInZone = isPointInLoadedGeoJSON(lng, lat);
            
            if (isInZone) {
                tooltip
                    .text(translations.click_map_choose_point)
                    .css({
                        'background': '#fff',
                        'border-color': '#ccc',
                        'color': '#36443b'
                    });
            } else {
                tooltip
                    .text(translations.zone_out_of_bounds)
                    .css({
                        'background': '#fff3cd',
                        'border-color': '#ffc107',
                        'color': '#856404'
                    });
            }
        };
        
        map.on('mousemove', mapTooltipMouseMoveHandler);
    } else {
        // En mode mushroom, s'assurer que le tooltip affiche le message par défaut
        const tooltip = $('#map-point-tooltip');
        if (tooltip.length > 0) {
            tooltip
                .text(translations.click_map_choose_point)
                .css({
                    'background': '#fff',
                    'border-color': '#ccc',
                    'color': '#36443b'
                });
        }
    }
    
    if (DEBUG_MODE) {
        console.log('Point selection mode enabled');
    }
};

// Variable pour stocker le gestionnaire d'événement de la carte pour le tooltip
let mapTooltipMouseMoveHandler = null;

// Désactiver le mode de sélection de point
const disablePointSelection = () => {
    isPointSelectionMode = false;
    map.getCanvas().style.cursor = '';
    
    // Restaurer l'apparence du bouton
    $('#selectPointBtn').removeClass('btn-primary').prop('disabled', false);
    
    // Supprimer l'info-bulle et son gestionnaire d'événement
    $('#map-point-tooltip').remove();
    $(document).off('mousemove.pointTooltip');
    
    // Supprimer le gestionnaire d'événement de la carte si il existe
    if (mapTooltipMouseMoveHandler) {
        map.off('mousemove', mapTooltipMouseMoveHandler);
        mapTooltipMouseMoveHandler = null;
    }

    // Sur mobile, réouvrir la Bottom Sheet biotope en mode "plein" pour continuer les critères
    try {
        if (window.matchMedia && window.matchMedia('(max-width: 767.98px)').matches) {
            const overlay = document.getElementById('card-bottom-sheet-overlay');
            const biotopeCard = document.getElementById('biotope-selection-card');
            if (overlay && biotopeCard && typeof openCardBottomSheet === 'function') {
                // Toujours repasser en mode plein
                const sheet = overlay.querySelector('.card-bottom-sheet');
                if (sheet) {
                    sheet.classList.remove('collapsed');
                }
                overlay.style.height = 'calc(100dvh - 142px)';
                openCardBottomSheet(biotopeCard);
            }
        }
    } catch (e) {
        // no-op
    }
    
    if (DEBUG_MODE) {
        console.log('Point selection mode disabled');
    }
};

// Effacer le point sélectionné
const clearSelectedPoint = () => {
    if (selectedPointMarker) {
        selectedPointMarker.remove();
        selectedPointMarker = null;
    }
    
    // Supprimer les couches et la source du cercle de recherche
    if (map.getSource('search-radius')) {
        if (map.getLayer('search-radius-fill')) {
            map.removeLayer('search-radius-fill');
        }
        if (map.getLayer('search-radius-border')) {
            map.removeLayer('search-radius-border');
        }
        map.removeSource('search-radius');
    }
    
    searchRadiusCircle = null;
    selectedPoint = null;
    $('#selectedPointInfo').hide();
    $('#clearPointBtn').hide();
    $('#selectedLat, #selectedLng').val('');
    const searchZoneInput = $('#searchZoneInput, [id$="-search-zone-input"]');
    searchZoneInput.val('').removeClass('search-zone-input--point-required');
    
    if (DEBUG_MODE) {
        console.log('Selected point cleared');
    }
};

// Créer un marqueur pour le point sélectionné
const createPointMarker = (lng, lat) => {
    // Créer un élément HTML pour le marqueur
    const markerElement = document.createElement('div');
    markerElement.className = 'selected-point-marker';
    markerElement.innerHTML = '<i class="fas fa-map-marker-alt" style="color: #007bff; font-size: 24px;"></i>';
    
    return new maplibregl.Marker({ element: markerElement })
        .setLngLat([lng, lat])
        .addTo(map);
};

// Créer un cercle pour le rayon de recherche
const createSearchRadiusCircle = (lng, lat, radiusMeters = ADVANCED_SEARCH_RADIUS_METERS) => {
    // Créer un cercle avec Turf.js
    const center = turf.point([lng, lat]);
    const circle = turf.circle(center, radiusMeters / 1000, { units: 'kilometers' });
    
    // Ajouter la source et la couche pour le cercle
    if (map.getSource('search-radius')) {
        map.removeLayer('search-radius-fill');
        map.removeLayer('search-radius-border');
        map.removeSource('search-radius');
    }
    
    map.addSource('search-radius', {
        type: 'geojson',
        data: circle
    });
    
    map.addLayer({
        id: 'search-radius-fill',
        type: 'fill',
        source: 'search-radius',
        paint: {
            'fill-color': '#007bff',
            'fill-opacity': 0.1
        }
    });
    
    map.addLayer({
        id: 'search-radius-border',
        type: 'line',
        source: 'search-radius',
        paint: {
            'line-color': '#007bff',
            'line-width': 2,
            'line-opacity': 0.8
        }
    });
    
    // Hide circles only when mushroom card is active (user not viewing biotope form).
    // When connecté + non abonné, currentMapMode stays 'mushroom' but biotope form is visible ->
    // we still want to show the selection circles.
    const biotopeCard = document.getElementById('biotope-selection-card');
    const isBiotopeFormActive = biotopeCard && !biotopeCard.classList.contains('d-none');
    if (!isBiotopeFormActive) {
        map.setLayoutProperty('search-radius-fill', 'visibility', 'none');
        map.setLayoutProperty('search-radius-border', 'visibility', 'none');
    }
    
    return circle;
};

// Vérifier si un point est à l'intérieur du GeoJSON chargé
const isPointInLoadedGeoJSON = (lng, lat) => {
    if (!loadedMapGeoJSON || !loadedMapGeoJSON.features || loadedMapGeoJSON.features.length === 0) {
        // Si aucun GeoJSON n'est chargé, autoriser le point (compatibilité)
        return true;
    }
    
    // Créer un point avec Turf.js
    const point = turf.point([lng, lat]);
    
    // Vérifier si le point est dans l'une des features du GeoJSON
    for (const feature of loadedMapGeoJSON.features) {
        try {
            // Utiliser turf.booleanPointInPolygon pour vérifier si le point est dans le polygone
            if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') {
                if (turf.booleanPointInPolygon(point, feature)) {
                    return true;
                }
            } else if (feature.geometry.type === 'GeometryCollection') {
                // Pour une GeometryCollection, vérifier chaque géométrie Polygon/MultiPolygon
                for (const geom of feature.geometry.geometries) {
                    if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
                        if (turf.booleanPointInPolygon(point, turf.feature(geom))) {
                            return true;
                        }
                    }
                }
            } else if (feature.geometry.type === 'Point') {
                // Pour les points, vérifier la distance (tolérance de 1 mètre)
                const featurePoint = turf.point(feature.geometry.coordinates);
                const distance = turf.distance(point, featurePoint, { units: 'meters' });
                if (distance < 1) {
                    return true;
                }
            }
        } catch (error) {
            if (DEBUG_MODE) {
                console.warn('Error checking point in GeoJSON feature:', error);
            }
        }
    }
    
    return false;
};

// Gérer le clic sur la carte pour sélectionner un point
const handleMapClickForPointSelection = (e) => {
    if (!isPointSelectionMode) return;
    
    const { lng, lat } = e.lngLat;
    
    // En mode biotope, vérifier que le point est dans le GeoJSON chargé
    if (typeof window.currentMapMode !== 'undefined' && window.currentMapMode === 'biotope') {
        if (!isPointInLoadedGeoJSON(lng, lat)) {
            // Afficher un message d'erreur à l'utilisateur
            alert(translations.point_outside_zone_alert);
            if (DEBUG_MODE) {
                console.log('Point rejected (outside GeoJSON):', { lat, lng });
            }
            return;
        }
    }
    
    // Effacer l'ancien point s'il existe
    clearSelectedPoint();
    
    // Créer le nouveau marqueur
    selectedPointMarker = createPointMarker(lng, lat);
    
    // Créer le cercle de rayon
    searchRadiusCircle = createSearchRadiusCircle(lng, lat);
    
    // Mettre à jour les champs cachés (s'assurer que ce sont des nombres)
    $('#selectedLat').val(parseFloat(lat)).trigger('change');
    $('#selectedLng').val(parseFloat(lng)).trigger('change');
    
    // Afficher les coordonnées dans le champ de zone de recherche (readonly)
    const searchZoneInput = $('#searchZoneInput, [id$="-search-zone-input"]');
    searchZoneInput.val(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
    searchZoneInput.removeClass('search-zone-input--point-required');
    
    // Afficher les informations du point
    $('#pointCoordinates').text(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
    $('#selectedPointInfo').show();
    $('#clearPointBtn').show();
    
    // Désactiver le mode de sélection
    disablePointSelection();
    
    // Mettre à jour le bouton "Générer le biotope"
    if (typeof updateGenerateBiotopeButton === 'function') {
        updateGenerateBiotopeButton();
    }
    
    if (DEBUG_MODE) {
        console.log('Point selected:', { lat, lng });
    }
};

// Charger la recherche géographique avec le serveur Martin
const loadGeographicSearchWithMartin = (lat, lng, radius, filters) => {
    // S'assurer que les paramètres sont des nombres
    lat = parseFloat(lat);
    lng = parseFloat(lng);
    radius = parseInt(radius) || ADVANCED_SEARCH_RADIUS_METERS;
    
    // Limiter le rayon maximum à 5000 mètres (correspond à la limite SQL)
    radius = Math.min(radius, ADVANCED_SEARCH_RADIUS_METERS);
    
    if (isNaN(lat) || isNaN(lng)) {
        console.error('Invalid coordinates:', { lat, lng });
        return;
    }
    
    if (DEBUG_MODE) {
        console.log('Loading geographic search with Martin:', { lat, lng, radius, filters });
    }
    
    // Construire les paramètres de filtre pour Martin
    const martinParams = buildMartinFilters(filters);
    
    // Ajouter les paramètres géographiques
    // Envoyer le radius en mètres directement (la fonction SQL utilise ST_Distance_Sphere)
    // S'assurer que le radius est bien limité à 5000 mètres (double vérification)
    radius = Math.min(parseInt(radius) || ADVANCED_SEARCH_RADIUS_METERS, ADVANCED_SEARCH_RADIUS_METERS);
    const geographicParams = `lat=${lat}&lng=${lng}&radius=${radius}`;
    
    if (DEBUG_MODE) {
        console.log('Geographic params URL:', geographicParams);
    }
    
    // Ajouter les paramètres country et regions (liste depuis mosaics.json, séparée par virgules)
    // Utiliser le code pays de itemMap (ex: ch-ostschweiz -> ch) pour correspondre aux régions mosaïque
    let countryParam = '';
    if (martinParams && martinParams.includes('country=')) {
        countryParam = '';
    } else {
        let countryForZones = 'fr';
        if (typeof window.itemMap !== 'undefined' && window.itemMap) {
            const countryFromItemMap = String(window.itemMap).split('-')[0].toLowerCase();
            if (countryFromItemMap.length >= 2) {
                countryForZones = countryFromItemMap;
            }
        } else if (typeof window.countryCode !== 'undefined' && window.countryCode) {
            countryForZones = String(window.countryCode).toLowerCase();
        }
        countryParam = `country=${countryForZones}`;
    }
    let regionsParam = '';
    if (!martinParams || !martinParams.includes('regions=')) {
        const regions = typeof window.mosaicRegions !== 'undefined' && Array.isArray(window.mosaicRegions) && window.mosaicRegions.length > 0
            ? window.mosaicRegions
            : (typeof window.itemMap !== 'undefined' && window.itemMap
                ? [window.itemMap.replace(/^[a-z]{2}-/, '')]  // fallback: extraire la région d'itemMap (ex: fr-ain -> ain)
                : ['ain']);
        regionsParam = 'regions=' + encodeURIComponent(regions.join(','));
    }
    
    // Construire tous les paramètres
    const defaultParams = [countryParam, regionsParam].filter(p => p !== '').join('&');
    let allParams = martinParams ? martinParams : '';
    if (defaultParams) {
        allParams = allParams ? `${allParams}&${defaultParams}` : defaultParams;
    }
    allParams = allParams ? `${allParams}&${geographicParams}` : geographicParams;
    
    // URL du serveur Martin
    const martinUrl = MAP_ADVANCED_BASE_URL;
    const sourceId = 'geographic-search-source';
    const layerId = 'geographic-search-layer';
    
    // Nettoyer les anciennes couches
    if (map.getSource(sourceId)) {
        if (map.getLayer(layerId + '-fill')) {
            map.removeLayer(layerId + '-fill');
        }
        if (map.getLayer(layerId + '-border')) {
            map.removeLayer(layerId + '-border');
        }
        map.removeSource(sourceId);
    }
    
    // Ajouter la source Martin avec les filtres et paramètres géographiques
    map.addSource(sourceId, {
        type: 'vector',
        tiles: [`${martinUrl}/public.zones_filtered/{z}/{x}/{y}?${allParams}`]
    });
    
    // Ajouter les couches (sans filtre géographique pour l'instant)
    map.addLayer({
        id: layerId + '-fill',
        type: 'fill',
        source: sourceId,
        'source-layer': 'zones_filtered',
        paint: {
            'fill-color': '#28a745',
            'fill-opacity': 0.3
        }
    });
    
    map.addLayer({
        id: layerId + '-border',
        type: 'line',
        source: sourceId,
        'source-layer': 'zones_filtered',
        paint: {
            'line-color': '#1e7e34',
            'line-width': 2
        }
    });
    
    // Hide biotope zones if we're in mushroom mode
    if (typeof window.currentMapMode !== 'undefined' && window.currentMapMode === 'mushroom') {
        map.setLayoutProperty(layerId + '-fill', 'visibility', 'none');
        map.setLayoutProperty(layerId + '-border', 'visibility', 'none');
    }
    
    // Centrer la carte sur le point sélectionné avec un zoom adapté au rayon
    const zoomLevel = Math.max(8, Math.min(15, Math.round(15 - Math.log2(radius / 1000))));
    map.flyTo({
        center: [lng, lat],
        zoom: zoomLevel
    });
    
    // Ajouter un cercle de limitation visuelle pour montrer la zone de recherche
    if (map.getSource('search-limit-circle')) {
        map.removeLayer('search-limit-circle-fill');
        map.removeLayer('search-limit-circle-border');
        map.removeSource('search-limit-circle');
    }
    
    // Créer un cercle de limitation avec Turf.js
    const center = turf.point([lng, lat]);
    const circle = turf.circle(center, radius / 1000, { units: 'kilometers' });
    
    map.addSource('search-limit-circle', {
        type: 'geojson',
        data: circle
    });
    
    map.addLayer({
        id: 'search-limit-circle-fill',
        type: 'fill',
        source: 'search-limit-circle',
        paint: {
            'fill-color': '#ff6b6b',
            'fill-opacity': 0.1
        }
    });
    
    map.addLayer({
        id: 'search-limit-circle-border',
        type: 'line',
        source: 'search-limit-circle',
        paint: {
            'line-color': '#ff6b6b',
            'line-width': 2,
            'line-dasharray': [2, 2]
        }
    });
    
    // Hide circles if we're in mushroom mode
    if (typeof window.currentMapMode !== 'undefined' && window.currentMapMode === 'mushroom') {
        map.setLayoutProperty('search-limit-circle-fill', 'visibility', 'none');
        map.setLayoutProperty('search-limit-circle-border', 'visibility', 'none');
    }
    
    // Afficher les informations dans la sidebar / plein écran mobile
    $('.map-info').removeClass('d-none');
    adjustMapInfoForMobile();
    $('[data-element="area-info"]').html(`
        <div class="geographic-search-results">
            <h5><i class="fas fa-map-marker-alt text-primary me-2"></i>${translations.geographic_search_title}</h5>
            <p class="small text-muted mb-3">
                Centre: ${lat.toFixed(6)}, ${lng.toFixed(6)}<br>
                Rayon: ${radius/1000}km<br>
                ${translations.filters_applied} ${martinParams || translations.filters_none}
            </p>
            <div class="alert alert-info">
                <i class="fas fa-info-circle me-2"></i>
                ${translations.geographic_search_info}
            </div>
        </div>
    `);
    
    // Ajouter un gestionnaire de clic pour les zones Martin
    map.on('click', layerId + '-fill', function(e) {
        const features = map.queryRenderedFeatures(e.point, { layers: [layerId + '-fill'] });
        if (features.length > 0) {
            const feature = features[0];
            const properties = feature.properties;
            
            // Afficher les détails de la zone dans la sidebar
            let html = `
                <div class="zone-details">
                    <h5><i class="fas fa-map-marker-alt text-success me-2"></i>Zone sélectionnée</h5>
                    <div class="zone-properties">
            `;
            
            // Afficher les propriétés de la zone
            Object.entries(properties).forEach(([key, value]) => {
                if (value !== null && value !== '' && value !== undefined) {
                    const label = getFieldLabel(key);
                    const formattedValue = formatFieldValue(key, value);
                    if (formattedValue !== null) {
                        html += `<div class="small mb-1"><strong>${label}:</strong> ${formattedValue}</div>`;
                    }
                }
            });
            
            html += `
                    </div>
                    <div class="mt-2">
                        <small class="text-muted">
                            <i class="fas fa-info-circle me-1"></i>
                            Coordonnées: ${e.lngLat.lat.toFixed(6)}, ${e.lngLat.lng.toFixed(6)}
                        </small>
                    </div>
                </div>
            `;
            
            $('[data-element="area-info"]').html(html);
        }
    });
    
    // Changer le curseur au survol des zones
    map.on('mouseenter', layerId + '-fill', function() {
        map.getCanvas().style.cursor = 'pointer';
    });
    
    map.on('mouseleave', layerId + '-fill', function() {
        map.getCanvas().style.cursor = '';
    });
    
    // Garder le loading jusqu'à ce que les tuiles soient chargées et rendues (idle = carte prête)
    let loadingHidden = false;
    const hideBiotopeLoading = () => {
        if (loadingHidden) return;
        loadingHidden = true;
        map.off('idle', onMapIdle);
        LoadingAnimation.complete();
        $('.map-loading').css('display', 'none');
    };
    
    const onMapIdle = () => {
        if (DEBUG_MODE) {
            console.log('Geographic search: map idle, tiles rendered');
        }
        hideBiotopeLoading();
    };
    
    map.once('idle', onMapIdle);
    
    // Timeout de sécurité (10s) au cas où les tuiles ne chargent pas
    setTimeout(hideBiotopeLoading, 10000);
    
    if (DEBUG_MODE) {
        console.log('Geographic search with Martin initiated (waiting for tiles)');
    }
};

// Construire les paramètres de filtre pour Martin
const buildMartinFilters = (filters) => {
    const params = {};
    
    if (DEBUG_MODE) {
        console.log('buildMartinFilters - Input filters:', filters);
    }
    
    // Regrouper les valeurs multiples par paramètre
    filters.forEach(filter => {
        if (filter.value === '' || filter.value === null || filter.value === undefined) {
            return;
        }

        // Extraire le nom du paramètre sans les crochets
        // Enlever 'filter[' au début et tous les crochets restants
        let paramName = filter.name.replace(/^filter\[/, '').replace(/[\[\]]/g, '');

        // Convertir tree en trees
        if (paramName === 'tree') {
            paramName = 'trees';
        }

        // Transformer la valeur selon le type de paramètre
        let paramValue = filter.value;
        
        // Pour ph : convertir les décimales avec virgules en valeurs entières x10
        // Ex: "3,0~8,0" -> ph_min=30&ph_max=80 (la fonction SQL attend des valeurs x10)
        if (paramName === 'ph') {
            // Remplacer les virgules par des points, puis multiplier par 10
            paramValue = paramValue.replace(/,/g, '.'); // Remplacer virgules par points
            if (paramValue.includes('~')) {
                // Format range: "3.0~8.0" -> ph_min=30&ph_max=80
                const parts = paramValue.split('~');
                const min = Math.round(parseFloat(parts[0]) * 10);
                const max = Math.round(parseFloat(parts[1]) * 10);
                params['ph_min'] = min;
                params['ph_max'] = max;
            } else {
                // Format simple: "3.0" -> ph_min=30&ph_max=30
                const value = Math.round(parseFloat(paramValue) * 10);
                params['ph_min'] = value;
                params['ph_max'] = value;
            }
            // Ne pas continuer le traitement normal pour ph
            return;
        }
        
        // Pour elevation : s'assurer que les valeurs sont des entiers (remplacer virgules par points si nécessaire)
        // Format: "2000~2500" -> elev_min=2000&elev_max=2500
        if (paramName === 'elevation') {
            paramValue = paramValue.replace(/,/g, '.'); // Remplacer virgules par points
            if (paramValue.includes('~')) {
                // Format range: s'assurer que les valeurs sont des entiers
                const parts = paramValue.split('~');
                const min = Math.round(parseFloat(parts[0]));
                const max = Math.round(parseFloat(parts[1]));
                params['elev_min'] = min;
                params['elev_max'] = max;
            } else {
                // Format simple: s'assurer que la valeur est un entier
                const value = Math.round(parseFloat(paramValue));
                params['elev_min'] = value;
                params['elev_max'] = value;
            }
            // Ne pas continuer le traitement normal pour elevation
            return;
        }

        // Si le paramètre existe déjà, ajouter à la liste des valeurs
        if (params[paramName]) {
            if (Array.isArray(params[paramName])) {
                params[paramName].push(paramValue);
            } else {
                params[paramName] = [params[paramName], paramValue];
            }
        } else {
            // Sinon, initialiser avec la première valeur
            params[paramName] = paramValue;
        }
    });

    // Construire la chaîne de requête
    const queryParts = [];
    for (const [key, value] of Object.entries(params)) {
        if (Array.isArray(value)) {
            // Pour les valeurs multiples, les joindre avec des virgules
            queryParts.push(`${key}=${encodeURIComponent(value.join(','))}`);
        } else {
            queryParts.push(`${key}=${encodeURIComponent(value)}`);
        }
    }
    
    const result = queryParts.join('&');
    
    if (DEBUG_MODE) {
        console.log('buildMartinFilters - Result:', result);
    }
    
    return result;
};

// Afficher les résultats de la recherche géographique
const displayGeographicSearchResults = (zones, centerLat, centerLng, radius) => {
    if (DEBUG_MODE) {
        console.log('Displaying geographic search results:', zones);
    }
    
    // Créer un GeoJSON avec les zones trouvées
    const features = zones.map((zone, index) => {
        let geometry = zone.geometry;
        
        // Si la géométrie est une chaîne, essayer de la parser
        if (typeof geometry === 'string') {
            try {
                geometry = JSON.parse(geometry);
            } catch (e) {
                console.warn('Failed to parse geometry:', e);
                return null;
            }
        }
        
        return {
            type: "Feature",
            properties: { 
                zoneIndex: index,
                ...zone
            },
            geometry: geometry
        };
    }).filter(feature => feature !== null);
    
    if (features.length === 0) {
        console.warn('No valid geometries found in search results');
        return;
    }
    
    const geoJson = {
        type: "FeatureCollection",
        features: features
    };
    
    // Nettoyer les anciennes couches
    if (map.getSource('geographic-search-results')) {
        if (map.getLayer('geographic-search-fill')) {
            map.removeLayer('geographic-search-fill');
        }
        if (map.getLayer('geographic-search-border')) {
            map.removeLayer('geographic-search-border');
        }
        map.removeSource('geographic-search-results');
    }
    
    // Ajouter la source et les couches
    map.addSource('geographic-search-results', {
        type: 'geojson',
        data: geoJson
    });
    
    map.addLayer({
        id: 'geographic-search-fill',
        type: 'fill',
        source: 'geographic-search-results',
        paint: {
            'fill-color': '#28a745',
            'fill-opacity': 0.3
        }
    });
    
    map.addLayer({
        id: 'geographic-search-border',
        type: 'line',
        source: 'geographic-search-results',
        paint: {
            'line-color': '#1e7e34',
            'line-width': 2
        }
    });
    
    // Hide biotope zones if we're in mushroom mode
    if (typeof window.currentMapMode !== 'undefined' && window.currentMapMode === 'mushroom') {
        map.setLayoutProperty('geographic-search-fill', 'visibility', 'none');
        map.setLayoutProperty('geographic-search-border', 'visibility', 'none');
    }
    
    // Afficher les informations dans la sidebar
    let html = `
        <div class="geographic-search-results">
            <h5><i class="fas fa-map-marker-alt text-primary me-2"></i>${translations.geographic_search_title}</h5>
            <p class="small text-muted mb-3">
                Centre: ${centerLat.toFixed(6)}, ${centerLng.toFixed(6)}<br>
                Rayon: ${radius/1000}km<br>
                Zones trouvées: ${zones.length}
            </p>
    `;
    
    zones.forEach((zone, index) => {
        html += `
            <div class="zone-item border rounded p-2 mb-2" data-zone-index="${index}">
                <h6>Zone ${index + 1}</h6>
        `;
        
        // Afficher les propriétés de la zone
        Object.entries(zone).forEach(([key, value]) => {
            if (key !== 'geometry' && key !== 'centroid' && value !== null && value !== '') {
                const label = getFieldLabel(key);
                const formattedValue = formatFieldValue(key, value);
                if (formattedValue !== null) {
                    html += `<div class="small"><strong>${label}:</strong> ${formattedValue}</div>`;
                }
            }
        });
        
        html += `</div>`;
    });
    
    html += `</div>`;
    
    $('.map-info').removeClass('d-none');
    adjustMapInfoForMobile();
    $('[data-element="area-info"]').html(html);
    
    // Ajouter les gestionnaires de clic pour les zones
    $('.zone-item').each(function() {
        const $item = $(this);
        $item.css('cursor', 'pointer');
        
        $item.off('click.zone-highlight').on('click.zone-highlight', function() {
            const zoneIndex = parseInt($(this).data('zone-index'));
            
            // Mettre en surbrillance la zone spécifique
            map.setFilter('geographic-search-fill', ['==', 'zoneIndex', zoneIndex]);
            map.setFilter('geographic-search-border', ['==', 'zoneIndex', zoneIndex]);
        });
    });
    
    // Centrer la carte sur les résultats
    if (features.length > 0) {
        const bounds = turf.bbox(geoJson);
        map.fitBounds(bounds, { padding: 50 });
    }
};




// Fonction de chargement des données simplifiée
function loadData(item, params) {
    Utils.closeAreaInfo();

    // Supprime les anciennes couches
    Object.values(vectorGrids).forEach((vg) => map.removeLayer(vg));
    vectorGrids = {};

    params = params.filter((p) => p.value !== '');

    if (DEBUG_MODE) {
        console.log('Loading data with params:', params);
    }

    if (params.length > 0 && params.some((p) => p.value === true || p.value !== '')) {
        $('.map-loading').css('display', 'flex');
        if (DEBUG_MODE) {
            console.log('Loading started');
        }
        
        // Ajouter l'animation de chargement
        showLoadingAnimation();
        removeCustomTiles();

        // Par défaut (si la variable n'est pas définie), on conserve le comportement existant.
        const hasPassionSubscription =
            (typeof window.hasPassionSubscription !== 'undefined') ? !!window.hasPassionSubscription : true;

        const stopLoading = () => {
            try {
                if (typeof LoadingAnimation !== 'undefined' && LoadingAnimation && typeof LoadingAnimation.complete === 'function') {
                    LoadingAnimation.complete();
                }
            } catch (e) {
                // no-op
            }
            $('.map-loading').css('display', 'none');
        };

        const showPassionUpsellModal = () => {
            // Pass Morchella/Boletus → modal upgrade ; aucun pass → choose-pass
            const modalElement = (window.hasPartialPass && document.getElementById('modalUpgradeCueillettePlus'))
                ? document.getElementById('modalUpgradeCueillettePlus')
                : document.getElementById('modalChoosePass') ||
                  document.getElementById('modalUpsell') ||
                  document.getElementById('modalLoginRequired');

            if (!modalElement) return;

            try {
                const Bs = typeof bootstrap !== 'undefined' ? bootstrap : window.bootstrap;
                if (Bs && Bs.Modal) {
                    const instance = Bs.Modal.getInstance(modalElement) || new Bs.Modal(modalElement);
                    instance.show();
                }
            } catch (e) {
                // no-op
            }
        };

        // Filtres réservés à l'abonnement Passion (recherche avancée / mode biotope)
        const blockedParamNames = new Set([
            'filter[elevation]',
            'filter[elevation_mean]',
            'filter[slope]',
            'filter[slope_mean]',
            'filter[tree][]',
            'filter[aspect][]',
            'filter[ph]',
        ]);

        const hasBlockedParams = params.some(p =>
            (blockedParamNames.has(p.name) || p.name.startsWith('filter[search_')) &&
            p.value !== '' && p.value !== null && typeof p.value !== 'undefined'
        );

        if (!hasPassionSubscription && hasBlockedParams) {
            showPassionUpsellModal();

            // Nettoyage "best-effort" des champs du formulaire pour éviter les relances involontaires
            try {
                $('input[name="filter[elevation]"]').val('');
                $('input[name="filter[elevation_mean]"]').val('');
                $('input[name="filter[slope]"]').val('');
                $('input[name="filter[slope_mean]"]').val('');
                $('input[name="filter[ph]"]').val('');
                $('input[name="filter[search_lat]"], input[name="filter[search_lng]"], input[name="filter[search_radius]"]').val('');
            } catch (e) {
                // no-op
            }

            // On retire les paramètres avancés, pour que la recherche "simple" reste possible
            params = params.filter(p => !blockedParamNames.has(p.name) && !p.name.startsWith('filter[search_'));

            // Si on a retiré tous les paramètres (cas typique: tentative de biotope sans champignon),
            // on stoppe proprement pour éviter des appels backend invalides.
            if (params.length === 0) {
                stopLoading();
                return false;
            }
        }

        const customSearch = isAdvancedSearch(params);
        const geographicSearch = isGeographicSearch(params);
        const mushroomHasTiles = true;

        // Vérifier si search_radius est présent (indique une recherche géographique)
        const hasSearchRadius = params.some(p => p.name === 'filter[search_radius]' && p.value !== '');
        
        // Si search_radius est présent, essayer de récupérer les coordonnées
        if (geographicSearch) {
            // Essayer de récupérer les coordonnées depuis les paramètres
            let lat = parseFloat(params.find(p => p.name === 'filter[search_lat]')?.value);
            let lng = parseFloat(params.find(p => p.name === 'filter[search_lng]')?.value);
            
            // Si les coordonnées ne sont pas dans les paramètres, essayer de les récupérer depuis les champs du formulaire
            if (isNaN(lat) || isNaN(lng)) {
                const latField = $('#selectedLat, [name="filter[search_lat]"]');
                const lngField = $('#selectedLng, [name="filter[search_lng]"]');
                if (latField.length > 0 && latField.val()) {
                    lat = parseFloat(latField.val());
                }
                if (lngField.length > 0 && lngField.val()) {
                    lng = parseFloat(lngField.val());
                }
            }
            
            // Si les coordonnées sont toujours manquantes, essayer de les récupérer depuis le marqueur
            if ((isNaN(lat) || isNaN(lng)) && selectedPointMarker) {
                const markerLngLat = selectedPointMarker.getLngLat();
                if (markerLngLat) {
                    lat = markerLngLat.lat;
                    lng = markerLngLat.lng;
                }
            }
            
            let radius = parseInt(params.find(p => p.name === 'filter[search_radius]')?.value) || ADVANCED_SEARCH_RADIUS_METERS;
            // Limiter le rayon maximum à 5000 mètres (correspond à la limite SQL)
            radius = Math.min(radius, ADVANCED_SEARCH_RADIUS_METERS);
            
            if (!isNaN(lat) && !isNaN(lng)) {
                // Si c'est aussi une recherche avancée, utiliser Martin avec les filtres avancés
                if (customSearch) {
                    loadGeographicSearchWithMartin(lat, lng, radius, params.filter(p => !p.name.startsWith('filter[search_')));
                } else {
                    // Recherche géographique simple
                    loadGeographicSearchWithMartin(lat, lng, radius, []);
                }
                return false;
            } else {
                // Si search_radius est présent mais sans coordonnées valides, ne rien faire
                // (pour éviter d'appeler l'endpoint backend avec seulement forest_type)
                if (DEBUG_MODE) {
                    console.warn('Search radius detected but invalid coordinates:', { lat, lng, hasSearchRadius, geographicSearch });
                }
                return false;
            }
        }

        if (customSearch || !mushroomHasTiles) {
            // Mode GeoJSON pour recherche personnalisée
            $.get(`${window.app_base_url}/map/data/${item}?${$.param(params)}`).done((raw) => {
                const t0 = performance.now();
                const jsonData = Utils.parseRawData(raw);
                const { data, bounds } = jsonData;

                removeCustomTiles();
                map.addSource('customTiles', { type: 'geojson', data });
                setupPolygonLayers();

                if (bounds && isFirstSearch) {
                    const boundsLatLng = bounds.match(/[+-]?([0-9]*[.])?[0-9]+/g);
                    map.fitBounds([
                        [boundsLatLng[0], boundsLatLng[1]],
                        [boundsLatLng[2], boundsLatLng[3]],
                    ]);
                    isFirstSearch = false;
                }

                if (DEBUG_MODE) {
                    console.log(`GeoJSON load in ${performance.now() - t0} milliseconds.`);
                    console.log('Loaded features count:', data.features.length);
                }
            });
            return false;
        }

        // Mode raster tiles - créer une source et une couche par département
        $.getJSON(`${window.app_base_url}/map/data/tiles/v5/${item}?${$.param(params)}`).done(({ tiles }) => {
            removeCustomTiles();
            
            // Mettre à jour la liste des tiles disponibles (dynamique)
            updateAvailableTiles(tiles);
            
            // Créer une source et une couche pour chaque tileset
            tiles.forEach((tile, index) => {
                try {
                    // Ajouter la source pour ce tileset
                    map.addSource(tile.sourceId, {
                        type: 'raster',
                        tiles: [tile.url],
                        tileSize: 256,
                        minzoom: 6,
                        maxzoom: 17
                    });
                    
                    // Ajouter la couche pour ce tileset
                    map.addLayer({
                        id: tile.layerId,
                        type: 'raster',
                        source: tile.sourceId
                    });
                    
                    if (DEBUG_MODE) {
                        console.log(`✅ Added source: ${tile.sourceId} with layer: ${tile.layerId}`);
                        console.log(`   URL: ${tile.url}`);
                    }
                } catch (error) {
                    console.error(`❌ Error adding source ${tile.sourceId}:`, error);
                }
            });
            
            // Gestionnaire pour détecter quand les sources sont chargées
            let loadedSources = 0;
            const totalSources = tiles.length;
            let loadingHidden = false;
            
            const hideLoading = () => {
                if (!loadingHidden) {
                    LoadingAnimation.complete();
                    loadingHidden = true;
                    if (DEBUG_MODE) {
                        console.log('Loading animation completed');
                    }
                }
            };
            
            const onSourceLoaded = (e) => {
                if (tiles.some(tile => tile.sourceId === e.sourceId) && e.isSourceLoaded) {
                    loadedSources++;
                    
                    // Mettre à jour la progression
                    const progress = Math.round((loadedSources / totalSources) * 100);
                    LoadingAnimation.updateProgress(progress, translations.loading_tiles_count.replace(':loaded', loadedSources).replace(':total', totalSources));
                    
                    if (DEBUG_MODE) {
                        console.log(`Source loaded: ${e.sourceId} (${loadedSources}/${totalSources})`);
                    }
                    if (loadedSources >= totalSources) {
                        hideLoading();
                        map.off('sourcedata', onSourceLoaded);
                    }
                }
            };
            
            // Événement idle de la carte comme fallback
            const onMapIdle = () => {
                if (DEBUG_MODE) {
                    console.log('Map idle - hiding loading');
                }
                hideLoading();
                map.off('idle', onMapIdle);
            };
            
            map.on('sourcedata', onSourceLoaded);
            map.on('idle', onMapIdle);
            
            // Timeout de sécurité (3 secondes max)
            setTimeout(() => {
                if (DEBUG_MODE) {
                    console.log('Loading timeout - forcing hide');
                }
                hideLoading();
            }, 3000);

            // Rafraîchit périodiquement le token
            if (intervalIdMap) clearInterval(intervalIdMap);
            intervalIdMap = setInterval(() => {
                $.getJSON(`${window.app_base_url}/map/data/tiles/v5/${item}?${$.param(params)}`)
                    .done(({ tiles }) => {
                        removeCustomTiles();
                        
                        // Recréer toutes les sources et couches
                        tiles.forEach((tile, index) => {
                            map.addSource(tile.sourceId, {
                                type: 'raster',
                                tiles: [tile.url],
                                tileSize: 256,
                                minzoom: 6,
                                maxzoom: 17
                            });
                            
                            map.addLayer({
                                id: tile.layerId,
                                type: 'raster',
                                source: tile.sourceId,
                                minzoom: 6,
                                maxzoom: 17
                            });
                        });
                        
                        // Gestionnaire pour le rafraîchissement des tokens
                        let loadedSources = 0;
                        const totalSources = tiles.length;
                        
                        const onSourceRefreshed = (e) => {
                            if (tiles.some(tile => tile.sourceId === e.sourceId) && e.isSourceLoaded) {
                                loadedSources++;
                                if (loadedSources >= totalSources) {
                                    map.off('sourcedata', onSourceRefreshed);
                                }
                            }
                        };
                        
                        map.on('sourcedata', onSourceRefreshed);
                    });
            }, 25 * 60 * 1000);

            // Utiliser le layerName du premier tile (tous devraient être identiques)
            const layerName = tiles[0]?.layerName;
            
            // Utiliser 'mushroom_data' comme nom de layer par défaut
            const sourceLayer = layerName || 'mushroom_data';
            currentLayerName = sourceLayer;
            
            // Les sources et couches sont déjà créées dans la boucle forEach ci-dessus
            // Pas besoin d'appeler setupPolygonLayers qui était pour l'ancienne approche
        });
    }
}

const removeCustomTiles = () => {
    // Supprimer toutes les tiles de champignons précédemment ajoutées
    if (availableTiles && availableTiles.length > 0) {
        availableTiles.forEach(tile => {
            // Supprimer la couche si elle existe
            if (map.getLayer(tile.layerId)) {
                try {
                    map.removeLayer(tile.layerId);
                    if (DEBUG_MODE) {
                        console.log(`✅ Removed layer: ${tile.layerId}`);
                    }
                } catch (error) {
                    console.warn(`⚠️ Error removing layer ${tile.layerId}:`, error);
                }
            }
            
            // Supprimer la source si elle existe
            if (map.getSource(tile.sourceId)) {
                try {
                    map.removeSource(tile.sourceId);
                    if (DEBUG_MODE) {
                        console.log(`✅ Removed source: ${tile.sourceId}`);
                    }
                } catch (error) {
                    console.warn(`⚠️ Error removing source ${tile.sourceId}:`, error);
                }
            }
        });
        
        // Réinitialiser la liste des tiles disponibles
        availableTiles = [];
    }
    
    // Supprimer toutes les couches de départements
    const layerIds = ['layer_dep_01', 'layer_dep_02', 'layer_dep_03', 'polygonsRaster'];
    layerIds.forEach(layerId => {
        if (map.getLayer(layerId)) {
            map.removeLayer(layerId);
        }
    });
    
    // Supprimer toutes les sources de départements
    const sourceIds = ['tileset_dep_01', 'tileset_dep_02', 'tileset_dep_03', 'customTilesRaster'];
    sourceIds.forEach(sourceId => {
        if (map.getSource(sourceId)) {
            map.removeSource(sourceId);
        }
    });
    
    // Supprimer les couches de recherche géographique Martin
    const martinLayerIds = ['geographic-search-layer-fill', 'geographic-search-layer-border'];
    martinLayerIds.forEach(layerId => {
        if (map.getLayer(layerId)) {
            map.removeLayer(layerId);
        }
    });
    
    if (map.getSource('geographic-search-source')) {
        map.removeSource('geographic-search-source');
    }
    
    // Supprimer le cercle de limitation de recherche
    const limitLayerIds = ['search-limit-circle-fill', 'search-limit-circle-border'];
    limitLayerIds.forEach(layerId => {
        if (map.getLayer(layerId)) {
            map.removeLayer(layerId);
        }
    });
    
    if (map.getSource('search-limit-circle')) {
        map.removeSource('search-limit-circle');
    }
};

// Rendre la fonction accessible globalement
window.removeCustomTiles = removeCustomTiles;

let clickedFeature = null;

const onCustomTilesClick = (e) => {
    const features = map.queryRenderedFeatures(e.point, { layers: ['polygonsLayer'] });
    let { id } = e.features[0].properties;

    // Gestion spéciale pour l'Allemagne
    if (website.country.iso_3166_2 === 'DE' && features[features.length - 1]) {
        if (clickedFeature !== features[features.length - 1].properties.id) {
            id = features[features.length - 1].properties.id;
        }
    }

    clickedFeature = id;
    
    if (DEBUG_MODE) {
        console.log('Clicked feature ID:', id);
        console.log('Feature source layer:', e.features[0].sourceLayer || 'no source layer');
        console.log('Feature properties:', e.features[0].properties);
    }

    // Réinitialise l'état de l'élément précédemment sélectionné
    if (currentId != 0) {
        const featureState = { source: 'customTiles', id: currentId };
        if (currentLayerName) featureState.sourceLayer = currentLayerName;
        map.setFeatureState(featureState, { selected: false });
    }
    
    currentId = id;
    
    // Met à jour le style de la couche
    map.setPaintProperty('polygonsLayer', 'fill-color', [
        'case', ['==', ['get', 'id'], id], '#1d79e8', '#FF6699'
    ]);
    map.setPaintProperty('polygonsBorders', 'line-color', [
        'case', ['==', ['get', 'id'], id], '#0972e3', '#ff3779'
    ]);
    map.setPaintProperty('polygonsLayer', 'fill-opacity', [
        'case', ['==', ['get', 'prio'], true], 0.6, ['!', ['has', 'prio']], 0.6, 0.25
    ]);

    // Affiche les détails du point sélectionné
    $.get(`${window.app_base_url}/map/point/${id}`).done((html) => {
        // if (tg?.isVisible) tg.finishTour();
        $('.map-info').removeClass('d-none');
        adjustMapInfoForMobile();
        $('[data-element="area-info"]').html(html);
        $('.js-click-latlng').html(
            `${Math.round(e.lngLat.lat * 100000) / 100000}, ${Math.round(e.lngLat.lng * 100000) / 100000}`
        );
        setTimeout(() => {
            if (!currentUser?.tour_guide_status?.map?.point && currentWebsite.fallback_locale === 'fr') {
                // tg.start('point');
            }
        }, 500);
    });
};

const onCustomTilesMouseEnter = () => map.getCanvas().style.cursor = 'pointer';
const onCustomTilesMouseLeave = () => map.getCanvas().style.cursor = '';

// Fonction pour ajouter les couches de polygones
function setupPolygonLayers(sourceLayer) {
    function onCustomTilesLoaded(e) {
        if (e.sourceId === 'customTilesRaster' && e.isSourceLoaded) {
            if (DEBUG_MODE) {
                console.log('Source loaded:', e.sourceId);
            }
            $('.map-loading').css('display', 'none');
            map.off('sourcedata', onCustomTilesLoaded);
        }
    }

    map.on('sourcedata', onCustomTilesLoaded);

    // Couche RASTER pour tous les zooms
    const rasterLayerConfig = {
        id: 'polygonsRaster',
        type: 'raster',
        source: 'customTilesRaster',
        minzoom: 6,
        maxzoom: 17
    };
    map.addLayer(rasterLayerConfig);

    // Note: Les tuiles rasterisées ne supportent pas les événements de clic
    // car elles sont des images PNG, pas des données vectorielles interactives
    
    if (!currentUser?.tour_guide_status?.map?.zone && currentWebsite.fallback_locale === 'fr') {
        // tg.start('zone');
    }
}


// ===== MAP CLICK HANDLER =====
map.on('click', function(e) {
    const { lng, lat } = e.lngLat;
    
    // Si on est en mode sélection de point, gérer la sélection
    if (isPointSelectionMode) {
        handleMapClickForPointSelection(e);
        return;
    }
    
    const isMushroomMode = typeof window.currentMapMode !== 'undefined' && window.currentMapMode === 'mushroom';
    const isBiotopeMode = typeof window.currentMapMode !== 'undefined' && window.currentMapMode === 'biotope';

    // Mode champignon : ne pas afficher area-detail-card si aucun champignon sélectionné
    if (isMushroomMode) {
        let selectedMushroom = null;
        const checkedMushroomInput = document.querySelector('input[name="filter_mushroom"]:checked');
        if (checkedMushroomInput) selectedMushroom = checkedMushroomInput.value;
        if (!selectedMushroom) {
            const activeMushroomCard = document.querySelector('.mushroom-selection-card .mushroom-item.active');
            if (activeMushroomCard) {
                const mushroomName = activeMushroomCard.getAttribute('data-mushroom-name') ||
                    activeMushroomCard.getAttribute('data-mushroom') ||
                    activeMushroomCard.querySelector('[data-mushroom-name]')?.getAttribute('data-mushroom-name');
                if (mushroomName) selectedMushroom = mushroomName;
            }
        }
        if (!selectedMushroom) {
            if (typeof window.hideBiotopeDetailCard === 'function') {
                window.hideBiotopeDetailCard();
            }
            return;
        }
    }

    // Mode biotope : ne pas afficher area-detail-card si la recherche n'a pas été lancée
    if (isBiotopeMode) {
        const hasBiotopeSearch = map.getSource('geographic-search-source') !== undefined;
        if (!hasBiotopeSearch) {
            if (typeof window.hideBiotopeDetailCard === 'function') {
                window.hideBiotopeDetailCard();
            }
            return;
        }
    }

    // Ne pas afficher area-detail-card si le clic est en dehors de la zone GeoJSON chargée
    if (!isPointInLoadedGeoJSON(lng, lat)) {
        if (typeof window.hideBiotopeDetailCard === 'function') {
            window.hideBiotopeDetailCard();
        }
        return;
    }
    
    // Sinon, comportement normal pour récupérer les données de la zone
    // Afficher l'animation de chargement
    showLoadingAnimation();
    
    // Récupérer le champignon sélectionné depuis le formulaire
    let selectedMushroom = null;
    const checkedMushroomInput = document.querySelector('input[name="filter_mushroom"]:checked');
    if (checkedMushroomInput) {
        selectedMushroom = checkedMushroomInput.value;
    }
    
    // Si pas trouvé dans le formulaire, essayer de le récupérer depuis la carte de sélection
    if (!selectedMushroom) {
        const activeMushroomCard = document.querySelector('.mushroom-selection-card .mushroom-item.active');
        if (activeMushroomCard) {
            const mushroomName = activeMushroomCard.getAttribute('data-mushroom-name') || 
                               activeMushroomCard.getAttribute('data-mushroom') ||
                               activeMushroomCard.querySelector('[data-mushroom-name]')?.getAttribute('data-mushroom-name');
            if (mushroomName) {
                selectedMushroom = mushroomName;
            }
        }
    }
    
    // Construire l'URL avec tous les paramètres (incl. locale pour les traductions côté serveur)
    const locale = (typeof currentWebsite !== 'undefined' && currentWebsite?.locale) ? currentWebsite.locale : '';
    let url = `${window.app_base_url}/map/click-data-html?item=${currentItem}&locale=${encodeURIComponent(locale)}`;
    
    // Préparer les paramètres de la requête
    const requestParams = {
        lat: lat,
        lng: lng,
        locale: locale
    };
    
    // Ajouter le champignon si sélectionné
    // Uniquement ajouter le champignon si on est en mode champignon
    if (typeof window.currentMapMode !== 'undefined' && window.currentMapMode === 'mushroom' && selectedMushroom) {
        url += `&mushroom=${encodeURIComponent(selectedMushroom)}`;
        requestParams.mushroom = selectedMushroom;
    }
    
    if (DEBUG_MODE) {
        console.log('Click data request:', { url, selectedMushroom, requestParams });
    }

    // --- Phase 2 tracking: Mushroom Clicked ---
    // NOTE: This handler fires on a bare map click (lat/lng), not on a GeoJSON
    // feature click. There is no feature object here, so:
    //   - mushroomId is always null (no feature.id / feature.properties.id)
    //   - isLocked is always false (feature.properties.is_locked not available
    //     until Phase 3 annotates the GeoJSON payload)
    // The geographic-search zone handler (map.on('click', layerId + '-fill', ...))
    // at ~line 2080 was intentionally skipped — it handles biotope overlay zones,
    // not mushroom sighting features.
    try {
        const geo = getCurrentGeo();
        const mushroomType = window.itemMap || null;
        const mushroomId = null; // no feature object in this handler
        const isLocked = false; // feature.properties.is_locked not yet in GeoJSON payload
        const userPlanForItem = (window.MH_USER_CONTEXT && window.MH_USER_CONTEXT.plan) || 'anonymous';

        tracking.mushroomClicked({
            mushroomType: mushroomType,
            mushroomId: mushroomId,
            isLocked: isLocked,
            userPlanForItem: userPlanForItem,
            viewedRegion: geo.region,
            viewedDept: geo.dept,
            zoomLevel: Math.round(map.getZoom() * 10) / 10,
        });
    } catch (err) {
        if (DEBUG_MODE) {
            console.warn('Mushroom Clicked tracking error:', err);
        }
    }
    reviewOnMushroomClicked();

    // Appeler l'endpoint Laravel pour récupérer le HTML
    $.get(url, requestParams)
        .done(function(html) {
            // Masquer l'animation de chargement
            LoadingAnimation.complete();
            
            // Remplacer directement le HTML de la carte area-detail-card
            const areaDetailCard = document.getElementById('area-detail-card');
            if (areaDetailCard) {
                areaDetailCard.outerHTML = html;
            }

            // Paywall Shown — fired when the server renders the card with available=false (overlay visible)
            const newAreaCard = document.getElementById('area-detail-card');
            if (newAreaCard && newAreaCard.getAttribute('data-available') === 'false' && window.MHTracking) {
                const pwiId = 'pwi_' + Math.random().toString(36).substring(2, 10) + Date.now().toString(36);
                const pwiCount = parseInt(sessionStorage.getItem('mh_paywalls_seen_count') || '0', 10) + 1;
                sessionStorage.setItem('mh_paywalls_seen_count', String(pwiCount));
                sessionStorage.setItem('mh_current_paywall_instance', pwiId);
                const geo = typeof getCurrentGeo === 'function' ? getCurrentGeo() : {};
                const userCtx = window.MH_USER_CONTEXT || {};
                window.MHTracking.paywallShown({
                    paywallInstanceId: pwiId,
                    trigger: 'area_detail_overlay',
                    paywalledFeature: 'area_detail_card',
                    paywalledItem: window.itemMap || null,
                    userPlanForItem: userCtx.plan || 'anonymous',
                    viewedRegion: geo.region || null,
                    viewedDept: geo.dept || null,
                    timeSinceRegisterDays: null,
                    priorPaywallsInSession: Math.max(0, pwiCount - 1),
                });
            }

            // Mettre à jour les coordonnées dynamiquement
            if (typeof updateAreaCardCoords === 'function') {
                updateAreaCardCoords(lat, lng);
            }
            
            // Initialiser les gestionnaires d'événements pour les coordonnées
            if (typeof initAreaCardCoordsHandlers === 'function') {
                setTimeout(initAreaCardCoordsHandlers, 50);
            }
            
            // Mettre à jour les coordonnées (ancien code pour compatibilité)
            $('.js-click-latlng').html(
                `${Math.round(lat * 100000) / 100000}, ${Math.round(lng * 100000) / 100000}`
            );
            
            // Traiter les géométries pour afficher les polygones
            processZoneGeometries();
            
            if (typeof window.showBiotopeDetailCard === 'function') {
                window.showBiotopeDetailCard();
            }
            
            // Déclencher le tour guide si nécessaire
            setTimeout(() => {
                if (!currentUser?.tour_guide_status?.map?.point && currentWebsite.fallback_locale === 'fr') {
                    // tg.start('point');
                }
            }, 500);
        })
        .fail(function(jqXHR) {
            // Masquer l'animation de chargement
            LoadingAnimation.hide();
            
            let message = translations.ajax_error;
            if (jqXHR && jqXHR.responseJSON && jqXHR.responseJSON.error) {
                message = jqXHR.responseJSON.error;
            }
            
            // Afficher l'erreur dans la sidebar / plein écran mobile
            $('.map-info').removeClass('d-none');
            adjustMapInfoForMobile();
            $('[data-element="area-info"]').html(`<div style="color: red;"><strong>${translations.error_label}</strong> ${message}</div>`);
            if (typeof window.hideBiotopeDetailCard === 'function') {
                window.hideBiotopeDetailCard();
            }
        });
});

// ===== AREA DETAIL CARD COORDS DYNAMIC UPDATE =====
/**
 * Met à jour dynamiquement les coordonnées dans la carte area-detail-card
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 */
function updateAreaCardCoords(lat, lng) {
    const coordsContainer = document.querySelector('[data-area-coords-container]');
    if (!coordsContainer) return;
    
    // Formater les coordonnées avec 6 décimales
    const formattedLat = Number(lat).toFixed(6);
    const formattedLng = Number(lng).toFixed(6);
    const coordsText = `${formattedLat}, ${formattedLng}`;
    
    // Mettre à jour les attributs data
    coordsContainer.setAttribute('data-area-lat', formattedLat);
    coordsContainer.setAttribute('data-area-lng', formattedLng);
    
    // Mettre à jour le texte des coordonnées
    const coordsValue = coordsContainer.querySelector('[data-area-coords]');
    if (coordsValue) {
        coordsValue.textContent = coordsText;
    }
    
    // Mettre à jour le lien Google Maps
    const mapsLink = coordsContainer.querySelector('[data-area-maps-link]');
    const mapsPlaceholder = coordsContainer.querySelector('[data-area-maps-placeholder]');
    
    if (mapsLink) {
        mapsLink.href = `https://maps.google.com/?q=${lat},${lng}`;
        mapsLink.classList.remove('d-none');
        if (mapsPlaceholder) mapsPlaceholder.classList.add('d-none');
    } else if (mapsPlaceholder) {
        // Créer le lien si il n'existe pas
        const newMapsLink = document.createElement('a');
        newMapsLink.href = `https://maps.google.com/?q=${lat},${lng}`;
        newMapsLink.target = '_blank';
        newMapsLink.rel = 'noopener noreferrer';
        newMapsLink.title = translations.open_in_google_maps;
        newMapsLink.className = 'area-detail-card__icon-link d-flex align-items-center';
        newMapsLink.style.color = '#404923';
        newMapsLink.setAttribute('data-area-maps-link', '');
        newMapsLink.innerHTML = `
            <svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
                <path d="M21 10.5c0 7.5-9 12-9 12s-9-4.5-9-12A9 9 0 0 1 12 1.5a9 9 0 0 1 9 9Z"/>
                <circle cx="12" cy="10.5" r="3"/>
            </svg>
        `;
        mapsPlaceholder.parentNode.replaceChild(newMapsLink, mapsPlaceholder);
    }
    
    // Mettre à jour le bouton de copie
    const copyButton = coordsContainer.querySelector('[data-area-copy-coords]');
    const copyPlaceholder = coordsContainer.querySelector('[data-area-copy-placeholder]');
    
    if (copyButton) {
        copyButton.classList.remove('d-none');
        if (copyPlaceholder) copyPlaceholder.classList.add('d-none');
    } else if (copyPlaceholder) {
        // Créer le bouton si il n'existe pas
        const newCopyButton = document.createElement('button');
        newCopyButton.type = 'button';
        newCopyButton.className = 'area-detail-card__icon-link btn btn-link p-0 m-0 text-decoration-none d-flex align-items-center';
        newCopyButton.title = translations.copy_coordinates;
        newCopyButton.style.color = '#404923';
        newCopyButton.setAttribute('data-area-copy-coords', '');
        newCopyButton.innerHTML = `
            <svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
                <rect x="9" y="9" width="13" height="13" rx="2"/>
                <rect x="2" y="2" width="13" height="13" rx="2"/>
            </svg>
        `;
        copyPlaceholder.parentNode.replaceChild(newCopyButton, copyPlaceholder);
    }
    
    // Réinitialiser les gestionnaires d'événements
    initAreaCardCoordsHandlers();
}

/**
 * Initialise les gestionnaires d'événements pour les coordonnées
 */
function initAreaCardCoordsHandlers() {
    // Gestionnaire pour le bouton de copie
    const copyButton = document.querySelector('[data-area-copy-coords]');
    if (copyButton) {
        // Supprimer les anciens gestionnaires
        const newCopyButton = copyButton.cloneNode(true);
        copyButton.parentNode.replaceChild(newCopyButton, copyButton);
        
        // Ajouter le nouveau gestionnaire
        newCopyButton.addEventListener('click', function(e) {
            e.preventDefault();
            const container = document.querySelector('[data-area-coords-container]');
            if (!container) return;
            
            const lat = container.getAttribute('data-area-lat');
            const lng = container.getAttribute('data-area-lng');
            
            if (lat && lng) {
                const coordsText = `${lat}, ${lng}`;
                navigator.clipboard.writeText(coordsText).then(() => {
                    // Feedback visuel
                    const svg = newCopyButton.querySelector('svg');
                    if (svg) {
                        svg.classList.add('text-success');
                        setTimeout(() => {
                            svg.classList.remove('text-success');
                        }, 1000);
                    }
                }).catch(err => {
                    console.error('Erreur lors de la copie:', err);
                });
            }
        });
    }
}

// Exposer les fonctions globalement
window.updateAreaCardCoords = updateAreaCardCoords;
window.initAreaCardCoordsHandlers = initAreaCardCoordsHandlers;

// Initialiser les gestionnaires au chargement du DOM
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAreaCardCoordsHandlers);
} else {
    initAreaCardCoordsHandlers();
}

// ===== ZONE GEOMETRY PROCESSING =====
function processZoneGeometries() {
    const geometryScript = document.getElementById('zone-geometries');
    if (!geometryScript) return;
    
    try {
        const geometryData = JSON.parse(geometryScript.textContent);
        
        // Clear existing polygons
        if (map.getSource('clicked-zones')) {
            if (map.getLayer('clicked-zones-layer')) {
                map.removeLayer('clicked-zones-layer');
            }
            if (map.getLayer('clicked-zones-border')) {
                map.removeLayer('clicked-zones-border');
            }
            map.removeSource('clicked-zones');
        }
        
        if (geometryData.length > 0) {
            // Create GeoJSON from geometries with validation
            const features = geometryData.map(function(zone) {
                // Validate and fix geometry if needed
                let geometry = zone.geometry;
                
                // If geometry is a string, try to parse it
                if (typeof geometry === 'string') {
                    try {
                        geometry = JSON.parse(geometry);
                    } catch (e) {
                        console.warn('Failed to parse geometry string:', e);
                        return null;
                    }
                }
                
                // Validate geometry structure
                if (!geometry || !geometry.type || !geometry.coordinates) {
                    console.warn('Invalid geometry structure:', geometry);
                    return null;
                }
                
                return {
                    type: "Feature",
                    properties: { zoneIndex: zone.index },
                    geometry: geometry
                };
            }).filter(feature => feature !== null); // Remove invalid features
            
            if (features.length === 0) {
                console.warn('No valid geometries found');
                return;
            }
            
            const geoJson = {
                type: "FeatureCollection",
                features: features
            };
            
            // Validate the complete GeoJSON before adding to map
            console.log('GeoJSON to add:', geoJson);
            
            // Add source and layer to map
            map.addSource('clicked-zones', {
                type: 'geojson',
                data: geoJson
            });
            
            map.addLayer({
                id: 'clicked-zones-layer',
                type: 'fill',
                source: 'clicked-zones',
                paint: {
                    'fill-color': '#1d79e8',
                    'fill-opacity': 0.3
                }
            });
            
            map.addLayer({
                id: 'clicked-zones-border',
                type: 'line',
                source: 'clicked-zones',
                paint: {
                    'line-color': '#0972e3',
                    'line-width': 2
                }
            });
            
            // Add click handlers to zone items
            $('.zone-item').each(function() {
                const $item = $(this);
                $item.css('cursor', 'pointer');
                
                $item.off('click.zone-highlight').on('click.zone-highlight', function() {
                    const zoneIndex = parseInt($(this).data('zone-index'));
                    
                    // Highlight specific zone
                    map.setFilter('clicked-zones-layer', ['==', 'zoneIndex', zoneIndex]);
                    map.setFilter('clicked-zones-border', ['==', 'zoneIndex', zoneIndex]);
                });
            });
        }
    } catch (error) {
        console.error('Error processing zone geometries:', error);
    }
}

// Helper: ajuster l'affichage de la carte de détail (map-info) en plein écran sur mobile
function adjustMapInfoForMobile() {
    try {
        const isMobile = window.matchMedia && window.matchMedia('(max-width: 767.98px)').matches;
        if (!isMobile) return;

        const mapInfo = document.querySelector('.map-info');
        if (!mapInfo) return;

        mapInfo.style.position = 'fixed';
        mapInfo.style.top = '78px';
        mapInfo.style.left = '0';
        mapInfo.style.right = '0';
        mapInfo.style.width = '100%';
        mapInfo.style.height = 'calc(100dvh - 78px)';
    } catch (e) {
        // no-op
    }
}

// ===== ÉVÉNEMENTS =====
$(document).ready(() => {
    // Zoom sur le pays au chargement
    if (!countryFeature) {
        $.getJSON('/world.json', (geoJSONdata) => {
            geoJSONdata.features.forEach((feature) => {
                if (feature.properties.name === website.country.name) {
                    const bounds = turf.bbox(feature);
                    map.fitBounds(bounds, { padding: 20 });
                }
            });
        });
    }

    // Helper: mettre la Bottom Sheet en mode réduit sur mobile
    function collapseBottomSheetOnMobile() {
        try {
            const isMobile = window.matchMedia && window.matchMedia('(max-width: 767.98px)').matches;
            if (!isMobile) return;

            const overlay = document.getElementById('card-bottom-sheet-overlay');
            if (!overlay) return;

            const sheet = overlay.querySelector('.card-bottom-sheet');
            if (!sheet) return;

            const header = sheet.querySelector('.card-bottom-sheet-header');
            sheet.classList.add('collapsed');
            if (header) {
                overlay.style.height = `${header.offsetHeight}px`;
            }
        } catch (e) {
            // no-op: ne jamais casser la recherche si le bottom sheet n'existe pas
        }
    }

    // Gestion du formulaire de recherche
    $('.form-map').on('submit', function () {
        const params = [];
        const isBiotopeMode =
            (typeof window.currentMapMode !== 'undefined' && window.currentMapMode === 'biotope');

        $(this).serializeArray().forEach((field) => {
            if (field.name === 'filter_mushroom') {
                params.push({ name: `filter[${field.value}]`, value: true });
                return;
            }

            // En mode champignon : n'envoyer que le filtre champignon, ignorer tous les filtres biotope
            if (!isBiotopeMode) {
                return;
            }

            params.push(field);
        });
        loadData(currentItem, params);
        return false;
    });

    // Déclenchement de la recherche lors d'un changement de filtre
    $('[name="filter_mushroom"]:not([disabled])')
        .on('change', function () {
            // Sur mobile, replier la Bottom Sheet après choix du champignon
            collapseBottomSheetOnMobile();
            if ($('.js-btn-show-map').is(':visible')) {
                $('.sidebar').addClass('sidebar-closed');
            }
            $('.form-map').submit();
        });

    // Gestion du clic sur le filtre "mushroom"
    $(document).on('click', '[name="filter_mushroom"]:not([disabled])', function (e) {
        const $this = $(this);

        // Si le champignon est "libre" et que l'utilisateur n'est pas connecté,
        // on retrouve le .mushroom-item associé pour récupérer data-free et data-available.
        try {
            const mushroomItem = $this.closest('.mushroom-item');
            const isFree = mushroomItem && mushroomItem.attr('data-free') === '1';
            const isAvailable = mushroomItem && mushroomItem.attr('data-available') === '1';
            const isLoggedIn = !!(window.currentUser && window.currentUser.email);

            if (isFree && (!isLoggedIn || !isAvailable)) {
                // Empêcher la sélection directe, on laisse la modal gérer la suite
                e.preventDefault();
                e.stopImmediatePropagation();

                // Modal selon pass : upgrade (Passion) ou choose-pass (autre pass)
                const getModal = typeof window.getMushroomModalElement === 'function' ? window.getMushroomModalElement : null;
                const modalElement = getModal && mushroomItem && mushroomItem.length
                    ? getModal(mushroomItem[0])
                    : document.getElementById('modalLoginRequired') ||
                      document.getElementById('modalUpsell') ||
                      document.getElementById('modalChoosePass');
                if (modalElement) {
                    try {
                        const Bs = typeof bootstrap !== 'undefined' ? bootstrap : window.bootstrap;
                        if (Bs && Bs.Modal) {
                            const instance = Bs.Modal.getInstance(modalElement) || new Bs.Modal(modalElement);
                            instance.show();
                        }
                    } catch (err) {
                        // no-op
                    }
                }
                return;
            }
        } catch (err) {
            // no-op: en cas d'erreur, on retombe sur le comportement standard
        }

        if ($this.hasClass('imChecked')) {
            $this.removeClass('imChecked').prop('checked', false).change();
        } else {
            $('[name="filter_mushroom"]').removeClass('imChecked');
            $this.prop('checked', true).addClass('imChecked');
        }
    });

    // Ouvrir la modale d'inscription (au lieu de la page d'inscription) au clic sur le CTA
    // de l'overlay biotope (affiché uniquement aux utilisateurs non connectés)
    $(document).on('click', '.js-biotope-overlay-cta', function (e) {
        const redirectUrl = $(this).attr('href');
        if (!redirectUrl || redirectUrl === '#') {
            return;
        }

        e.preventDefault();

        [document.getElementById('loginModalForm'), document.getElementById('registerModalForm')].forEach((form) => {
            if (!form) return;
            let redirectInput = form.querySelector('input[name="redirect"]');
            if (!redirectInput) {
                redirectInput = document.createElement('input');
                redirectInput.type = 'hidden';
                redirectInput.name = 'redirect';
                form.appendChild(redirectInput);
            }
            redirectInput.value = redirectUrl;
        });

        sessionStorage.setItem('mushroomRedirectUrl', redirectUrl);

        const modalElement = document.getElementById('modalLoginRequired');
        if (modalElement) {
            try {
                const Bs = typeof bootstrap !== 'undefined' ? bootstrap : window.bootstrap;
                if (Bs && Bs.Modal) {
                    const instance = Bs.Modal.getInstance(modalElement) || new Bs.Modal(modalElement);
                    instance.show();
                }
            } catch (err) {
                // no-op
            }
        }
    });

    $('[name="filter_mushroom"]:checked').change();

    // Pré-sélectionner le champignon depuis l'URL si fourni
    if (typeof selectedMushroomFromUrl !== 'undefined' && selectedMushroomFromUrl) {
        const mushroomInput = $(`input[name="filter_mushroom"][value="${selectedMushroomFromUrl}"]`);
        if (mushroomInput.length > 0) {
            const mushroomItem = mushroomInput.closest('.mushroom-item');
            const isAvailable = mushroomItem.attr('data-available') === '1';
            const isFree = mushroomItem.attr('data-free') === '1';
            
            // Si le champignon n'est pas disponible ou est libre mais l'utilisateur n'est pas connecté
            if (!isAvailable || (isFree && (!currentUser || !currentUser.email))) {
                // Construire l'URL de redirection avec le champignon
                // Utiliser currentItem pour construire l'URL correctement : /{locale}/map/{item}/{mushroom}
                // currentItem est défini dans le script inline de mapV5.blade.php
                const item = typeof window.currentItem !== 'undefined' ? window.currentItem : (typeof currentItem !== 'undefined' ? currentItem : '');
                const appUrlPrefix = (typeof window.app_url !== 'undefined' && window.app_url) ? '/' + window.app_url : '';
                const redirectUrl = appUrlPrefix + '/map/' + item + '/' + selectedMushroomFromUrl;
                
                // Stocker l'URL de redirection dans les formulaires de login/register
                const loginForm = document.getElementById('loginModalForm');
                const registerForm = document.getElementById('registerModalForm');
                
                if (loginForm) {
                    // Ajouter un champ caché pour l'URL de redirection
                    let redirectInput = loginForm.querySelector('input[name="redirect"]');
                    if (!redirectInput) {
                        redirectInput = document.createElement('input');
                        redirectInput.type = 'hidden';
                        redirectInput.name = 'redirect';
                        loginForm.appendChild(redirectInput);
                    }
                    redirectInput.value = redirectUrl;
                }
                
                if (registerForm) {
                    // Ajouter un champ caché pour l'URL de redirection
                    let redirectInput = registerForm.querySelector('input[name="redirect"]');
                    if (!redirectInput) {
                        redirectInput = document.createElement('input');
                        redirectInput.type = 'hidden';
                        redirectInput.name = 'redirect';
                        registerForm.appendChild(redirectInput);
                    }
                    redirectInput.value = redirectUrl;
                }
                
                // Stocker aussi dans sessionStorage pour les connexions sociales
                sessionStorage.setItem('mushroomRedirectUrl', redirectUrl);
                
                // Afficher la modal appropriée (upgrade ou choose-pass selon pass utilisateur)
                const getModal = typeof window.getMushroomModalElement === 'function' ? window.getMushroomModalElement : null;
                const modalElement = !currentUser
                    ? (document.getElementById('modalLoginRequired') || document.getElementById('modalChoosePass'))
                    : (getModal && mushroomItem && mushroomItem.length ? getModal(mushroomItem[0]) : document.getElementById('modalUpsell') || document.getElementById('modalChoosePass'));
                if (modalElement) {
                    try {
                        const Bs = typeof bootstrap !== 'undefined' ? bootstrap : window.bootstrap;
                        if (Bs && Bs.Modal) {
                            const instance = Bs.Modal.getInstance(modalElement) || new Bs.Modal(modalElement);
                            instance.show();
                        }
                    } catch (err) {
                        // no-op
                    }
                }
            } else {
                // Le champignon est disponible, le sélectionner
                $('[name="filter_mushroom"]').removeClass('imChecked');
                mushroomInput.prop('checked', true).addClass('imChecked');
                mushroomInput.closest('.mushroom-item').addClass('active');
                
                // Déclencher le change pour mettre à jour le formulaire et la recherche
                mushroomInput.change();
                
                // Afficher la carte de détail du champignon
                // Utiliser setTimeout pour s'assurer que le script inline est chargé
                setTimeout(() => {
                    if (typeof showMushroomDetailCard === 'function') {
                        showMushroomDetailCard(selectedMushroomFromUrl);
                    } else if (typeof window.showMushroomDetailCard === 'function') {
                        window.showMushroomDetailCard(selectedMushroomFromUrl);
                    }
                }, 200);
            }
        }
    }

    // Gestion des suppressions AJAX
    $(document).on('click', '.js-click-ajax-delete', function (e) {
        e.preventDefault();
        $.ajax({ type: 'DELETE', url: $(this).attr('href') });
    });

    // Gestion de la fermeture de la zone d'info
    $('[data-element="area-info"]').on('click', '[data-element="area-info-close"]', Utils.closeAreaInfo);
    
    // Gestion de la fermeture de la card biotope (bouton avec data-action="close-area-card")
    $(document).on('click', '[data-action="close-area-card"]', function(e) {
        e.preventDefault();
        // Appeler hideBiotopeDetailCard directement si disponible
        if (typeof window.hideBiotopeDetailCard === 'function') {
            window.hideBiotopeDetailCard();
        }
        // Appeler aussi closeAreaInfo pour nettoyer les zones et autres éléments
        Utils.closeAreaInfo();
    });

    // Initialisation des sliders noUiSlider
    $('[data-nouislider]').each(function () {
        const data = $(this).data();
        $(this).after(`<input type="hidden" name="${data.name}">`);
        noUiSlider.create(this, {
            connect: true,
            format: wNumb({ 
                decimals: typeof data.decimal !== 'undefined' ? data.decimal : 1,
                mark: ','
            }),
            tooltips: [true, true],
            range: { min: 0, max: 10000 },
            ...data,
        });

        this.noUiSlider.on('change', function (values) {
            $(`[name="${data.name}"]`).val(values.join('~'));
            // $('.form-map').submit();
        });

        this.noUiSlider.on('update', function (values) {
            if (data.labels) {
                $(`[data-name="${data.name}.min"]`).html(data.labels[values[0]]);
                $(`[data-name="${data.name}.max"]`).html(data.labels[values[1]]);
            } else {
                $(`[data-name="${data.name}.min"]`).html(values[0]);
                $(`[data-name="${data.name}.max"]`).html(values[1]);
            }
        });
    });

    // Initialisation des graphiques Chart.js
    $('.chart-growth-periods').each(function () {
        new Chart(this, {
            type: 'line',
            data: {
                labels: $(this).data('labels'),
                datasets: [{
                    label: translations.presence_label,
                    backgroundColor: '#4472C4',
                    borderColor: '#4472C4',
                    data: $(this).data('stats'),
                    fill: true,
                }],
            },
            options: {
                plugins: {
                    title: { display: true, text: $(this).data('title') },
                    legend: { display: false },
                },
                scales: {
                    x: { grid: { display: false } },
                    y: { beginAtZero: true, suggestedMin: 0, suggestedMax: 100 },
                },
                radius: 0,
            },
        });
    });

    // Gestion de la sidebar
    $('.js-sidebar-open').on('click', () => $('.sidebar').removeClass('sidebar-closed'));
    $('.js-sidebar-close').on('click', () => $('.sidebar').addClass('sidebar-closed'));
    $('.js-sidebar-icon-nav').on('click', function () {
        new bootstrap.Tab(document.querySelector(`button#${$(this).attr('id')}`)).show();
        $('.sidebar').removeClass('sidebar-closed');
    });

    // Gestion de la sélection de point géographique
    $('#selectPointBtn').on('click', function() {
        enablePointSelection();
    });

    // Activer le mode de sélection de point quand on clique dans le champ de recherche de zone
    $('#searchZoneInput, [id$="-search-zone-input"]').on('click', function() {
        $(this).removeClass('search-zone-input--point-required');
        enablePointSelection();
    });
    
    // Ajouter un curseur pointer au survol du champ
    $('#searchZoneInput, [id$="-search-zone-input"]').css('cursor', 'pointer');

    $('#clearPointBtn').on('click', function() {
        clearSelectedPoint();
        updateGenerateBiotopeButton();
    });
    
    // Fonction pour mettre à jour l'état du bouton "Générer le biotope"
    const updateGenerateBiotopeButton = () => {
        const generateBtn = $('#generate-biotope-btn');
        if (generateBtn.length === 0) return;
        
        // Vérifier si une zone est sélectionnée
        const lat = $('#selectedLat, [name="filter[search_lat]"]').val();
        const lng = $('#selectedLng, [name="filter[search_lng]"]').val();
        const hasZone = lat && lng && lat !== '' && lng !== '';
        
        // Vérifier si au moins un paramètre est sélectionné
        const hasTrees = $('#selected-trees-container .badge').length > 0;
        
        // Vérifier le slider pH (valeur par défaut: 4.8-7.2)
        let hasPh = false;
        const phSlider = document.getElementById('ph-slider');
        if (phSlider && phSlider.noUiSlider) {
            const phValues = phSlider.noUiSlider.get();
            hasPh = Math.abs(phValues[0] - 4.8) > 0.1 || Math.abs(phValues[1] - 7.2) > 0.1;
        }
        
        // Vérifier le slider elevation (valeur par défaut: 400-1600)
        let hasElevation = false;
        const elevationSlider = document.getElementById('elevation-slider');
        if (elevationSlider && elevationSlider.noUiSlider) {
            const elevationValues = elevationSlider.noUiSlider.get();
            hasElevation = elevationValues[0] !== 400 || elevationValues[1] !== 1600;
        }
        
        const hasAspect = $('[name="filter[aspect][]"]:checked').length > 0;
        
        const hasParameters = hasTrees || hasPh || hasElevation || hasAspect;
        
        // Le bouton est activé si une zone est sélectionnée ET au moins un paramètre est sélectionné
        if (hasZone && hasParameters) {
            generateBtn.removeClass('btn-disabled');
        } else {
            generateBtn.addClass('btn-disabled');
        }
    };
    
    // Mettre à jour le bouton au chargement
    updateGenerateBiotopeButton();
    
    // Mettre à jour le bouton quand une zone est sélectionnée
    $(document).on('change', '#selectedLat, #selectedLng, [name="filter[search_lat]"], [name="filter[search_lng]"]', function() {
        updateGenerateBiotopeButton();
    });
    
    // Mettre à jour le bouton quand les paramètres changent
    $(document).on('change', '[name="filter[tree][]"], [name="filter[aspect][]"]', function() {
        updateGenerateBiotopeButton();
    });
    
    // Mettre à jour le bouton quand les sliders changent
    $(document).on('update change', '#ph-slider, #elevation-slider', function() {
        updateGenerateBiotopeButton();
    });
    
    // Observer les changements dans le conteneur des arbres sélectionnés
    const treeContainer = document.getElementById('selected-trees-container');
    if (treeContainer) {
        const observer = new MutationObserver(function() {
            updateGenerateBiotopeButton();
        });
        observer.observe(treeContainer, { childList: true, subtree: true });
    }
    
    // Empêcher le clic si le bouton a la classe btn-disabled
    $(document).on('click', '#generate-biotope-btn.btn-disabled', function(e) {
        e.preventDefault();
        e.stopPropagation();

        const btn = $(this);
        const lat = $('#selectedLat, [name="filter[search_lat]"]').val();
        const lng = $('#selectedLng, [name="filter[search_lng]"]').val();
        const hasZone = lat && lng && lat !== '' && lng !== '';

        // Vérifier si au moins un paramètre est sélectionné
        const hasTrees = $('#selected-trees-container .badge').length > 0;
        let hasPh = false;
        const phSlider = document.getElementById('ph-slider');
        if (phSlider && phSlider.noUiSlider) {
            const phValues = phSlider.noUiSlider.get();
            hasPh = Math.abs(phValues[0] - 4.8) > 0.1 || Math.abs(phValues[1] - 7.2) > 0.1;
        }
        let hasElevation = false;
        const elevationSlider = document.getElementById('elevation-slider');
        if (elevationSlider && elevationSlider.noUiSlider) {
            const elevationValues = elevationSlider.noUiSlider.get();
            hasElevation = elevationValues[0] !== 400 || elevationValues[1] !== 1600;
        }
        const hasAspect = $('[name="filter[aspect][]"]:checked').length > 0;
        const hasParameters = hasTrees || hasPh || hasElevation || hasAspect;

        // Afficher le message d'erreur approprié
        let errorMsg = '';
        if (!hasZone && !hasParameters) {
            errorMsg = btn.data('error-both');
        } else if (!hasZone) {
            errorMsg = btn.data('error-zone');
        } else if (!hasParameters) {
            errorMsg = btn.data('error-params');
        }

        const errorEl = $('#biotope-error-message');
        if (errorMsg) {
            errorEl.text(errorMsg).removeClass('d-none');
        }

        if (!hasZone) {
            const searchInput = $('#searchZoneInput, [id$="-search-zone-input"]');
            searchInput.addClass('search-zone-input--point-required');
            // Remonter le scroll pour rendre le champ zone visible
            const scrollContainer = searchInput.closest('.biotope-selection-card__form-scroll');
            if (scrollContainer.length) {
                scrollContainer.animate({ scrollTop: 0 }, 300);
            }
        }
        return false;
    });

    // Masquer le message d'erreur quand l'état du bouton change
    $(document).on('change', '#selectedLat, #selectedLng, [name="filter[search_lat]"], [name="filter[search_lng]"], [name="filter[tree][]"], [name="filter[aspect][]"]', function() {
        $('#biotope-error-message').addClass('d-none');
    });
    $(document).on('update change', '#ph-slider, #elevation-slider', function() {
        $('#biotope-error-message').addClass('d-none');
    });

    // --- Phase 2 tracking: Map Filter Applied ---
    // Delegated listener — any element with [data-filter-type] triggers tracking on change.
    // Opt-in: the blade team annotates filter inputs with data-filter-type="<name>".
    // Note: Select2-wrapped selects trigger jQuery change events, not native DOM events;
    // those may need a jQuery-based listener (see jQuery-select2 adapter if needed).
    document.addEventListener('change', function (event) {
        var el = event.target;
        if (!el || !el.dataset || !el.dataset.filterType) return;

        var filterType = el.dataset.filterType;
        var filterValue;
        if (el.type === 'checkbox') {
            filterValue = el.checked;
        } else if (el.type === 'number' || el.type === 'range') {
            filterValue = parseFloat(el.value);
        } else {
            filterValue = el.value;
        }

        tracking.mapFilterApplied({
            mushroomType: window.itemMap || null,
            filterType: filterType,
            filterValue: filterValue,
            userPlanForItem: (window.MH_USER_CONTEXT && window.MH_USER_CONTEXT.plan) || 'anonymous',
        });
    });
});
